import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { collection, query, orderBy, onSnapshot, updateDoc, doc, addDoc, getDocs, serverTimestamp, setDoc, deleteDoc, getDoc, writeBatch } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { createNotification } from '../lib/notifications';
import { Link } from 'wouter';
import { Package, Tag, LogOut, CheckCircle, XCircle, Clock, Printer, Boxes, Edit2, Trash2, TrendingUp, DollarSign, PieChart, Calendar, QrCode, X, Bell, MailOpen, MessageSquare, Star, LayoutDashboard, Plus, MapPin, Users, Download, Upload, FileSpreadsheet, FileText, ClipboardList, Contact, Ticket, Monitor, Search, Workflow, Construction, Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { useProducts } from '../hooks/useProducts';
import { useCategories } from '../hooks/useCategories';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, Legend, AreaChart, Area, Cell, PieChart as RePieChart, Pie 
} from 'recharts';
import { format, startOfDay, subDays, eachDayOfInterval, isSameDay, startOfMonth, startOfYear, isWithinInterval } from 'date-fns';

function AnalyticsDashboard({ orders, onExportGst }: { orders: any[], onExportGst: () => void }) {
  const successfulOrders = orders.filter(o => o.status !== 'Returned');
  
  // Calculate stats
  const totalSales = successfulOrders.reduce((acc, o) => acc + (o.totalAmount || 0), 0);
  const totalGstCollected = successfulOrders.reduce((acc, o) => acc + (o.gstAmount || 0), 0);
  
  let totalProfit = 0;
  
  successfulOrders.forEach(order => {
    let orderProfit = 0;
    (order.items || []).forEach((item: any) => {
      const itemCost = (item.costPrice || 0) * item.quantity;
      const itemRevenue = (item.price || 0) * item.quantity;
      orderProfit += (itemRevenue - itemCost);
    });
    const discount = order.discountAmount || 0;
    totalProfit += (orderProfit - discount);
  });

  // Chart data: Last 14 days
  const last14Days = eachDayOfInterval({
    start: subDays(new Date(), 13),
    end: new Date()
  });

  const dailyData = last14Days.map(day => {
    const dayOrders = successfulOrders.filter(o => {
      const orderDate = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(o.createdAt || Date.now());
      return isSameDay(orderDate, day);
    });

    const sales = dayOrders.reduce((acc, o) => acc + (o.totalAmount || 0), 0);
    let profit = 0;
    dayOrders.forEach(o => {
      let p = 0;
      (o.items || []).forEach((i: any) => {
        p += ((i.price || 0) - (i.costPrice || 0)) * i.quantity;
      });
      profit += (p - (o.discountAmount || 0));
    });

    return {
      name: format(day, 'MMM dd'),
      sales,
      profit
    };
  });

  const monthlyData = [5, 4, 3, 2, 1, 0].map(m => {
    const monthStart = startOfMonth(subDays(new Date(), m * 30));
    const monthOrders = successfulOrders.filter(o => {
      const orderDate = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(o.createdAt || Date.now());
      return orderDate.getMonth() === monthStart.getMonth() && orderDate.getFullYear() === monthStart.getFullYear();
    });

    const sales = monthOrders.reduce((acc, o) => acc + (o.totalAmount || 0), 0);
    let profit = 0;
    monthOrders.forEach(o => {
      let p = 0;
      (o.items || []).forEach((i: any) => {
        p += ((i.price || 0) - (i.costPrice || 0)) * i.quantity;
      });
      profit += (p - (o.discountAmount || 0));
    });

    return {
      month: format(monthStart, 'MMM yy'),
      sales,
      profit
    };
  });

  const categoryPerformance = successfulOrders.reduce((acc: any, order) => {
    (order.items || []).forEach((item: any) => {
      const cat = item.category || 'Other';
      if (!acc[cat]) acc[cat] = { name: cat, sales: 0, profit: 0, quantity: 0 };
      acc[cat].sales += item.price * item.quantity;
      acc[cat].profit += (item.price - (item.costPrice || 0)) * item.quantity;
      acc[cat].quantity += item.quantity;
    });
    return acc;
  }, {});

  const pieData = Object.values(categoryPerformance);
  const COLORS = ['#2162eb', '#1e293b', '#64748b', '#94a3b8', '#cbd5e1'];

  return (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-xl hover:shadow-blue-900/5 transition-all">
          <div className="flex items-center justify-between mb-6">
            <div className="p-3 bg-blue-50 rounded-2xl group-hover:bg-blue-600 group-hover:text-white transition-all"><DollarSign className="w-5 h-5 text-blue-600 group-hover:text-white" /></div>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Revenue</span>
          </div>
          <div className="text-3xl font-bold text-slate-900">₹{totalSales.toLocaleString()}</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active Growth</span>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-xl hover:shadow-green-900/5 transition-all">
          <div className="flex items-center justify-between mb-6">
            <div className="p-3 bg-green-50 rounded-2xl group-hover:bg-green-600 group-hover:text-white transition-all"><TrendingUp className="w-5 h-5 text-green-600 group-hover:text-white" /></div>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Net Profit</span>
          </div>
          <div className="text-3xl font-bold text-green-600">₹{totalProfit.toLocaleString()}</div>
          <div className="mt-2 flex items-center gap-2">
             <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Margin: {totalSales > 0 ? ((totalProfit / totalSales) * 100).toFixed(1) : 0}%</span>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-xl hover:shadow-blue-900/5 transition-all">
          <div className="flex items-center justify-between mb-6">
            <div className="p-3 bg-blue-50 rounded-2xl group-hover:bg-blue-600 group-hover:text-white transition-all"><PieChart className="w-5 h-5 text-blue-600 group-hover:text-white" /></div>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">GST Sum</span>
          </div>
          <div className="text-3xl font-bold text-blue-600">₹{totalGstCollected.toLocaleString()}</div>
           <div className="mt-2 flex items-center gap-2">
             <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Accrued Tax Liability</span>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-xl hover:shadow-slate-900/5 transition-all">
          <div className="flex items-center justify-between mb-6">
            <div className="p-3 bg-slate-100 rounded-2xl group-hover:bg-slate-900 group-hover:text-white transition-all"><Package className="w-5 h-5 text-slate-900 group-hover:text-white" /></div>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Work</span>
          </div>
          <div className="text-3xl font-bold text-slate-900">{successfulOrders.length}</div>
          <div className="mt-2 flex items-center gap-2">
             <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">AOV: ₹{(totalSales / (successfulOrders.length || 1)).toFixed(0)}</span>
          </div>
        </div>
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-10 rounded-[40px] border border-slate-100 shadow-sm h-[450px]">
          <div className="flex justify-between items-center mb-8">
             <h3 className="text-xl font-bold text-slate-900">Revenue Stream</h3>
             <button onClick={onExportGst} className="p-2 hover:bg-slate-50 rounded-xl transition-colors">
               <Download className="w-5 h-5 text-slate-400" />
             </button>
          </div>
          <ResponsiveContainer width="100%" height="80%">
            <AreaChart data={dailyData}>
              <defs>
                <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2162eb" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#2162eb" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8fafc" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b', fontWeight: 'bold'}} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b', fontWeight: 'bold'}} />
              <Tooltip 
                contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', padding: '16px' }}
                itemStyle={{ fontSize: '12px', fontWeight: 'bold' }}
              />
              <Area type="monotone" dataKey="sales" stroke="#2162eb" fillOpacity={1} fill="url(#colorSales)" strokeWidth={3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-10 rounded-[40px] border border-slate-100 shadow-sm h-[450px]">
          <h3 className="text-xl font-bold text-slate-900 mb-8">Growth Trajectory</h3>
          <ResponsiveContainer width="100%" height="80%">
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8fafc" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b', fontWeight: 'bold'}} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b', fontWeight: 'bold'}} />
              <Tooltip 
                contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', padding: '16px' }}
                itemStyle={{ fontSize: '12px', fontWeight: 'bold' }}
              />
              <Bar dataKey="sales" fill="#2162eb" radius={[10, 10, 0, 0]} name="Revenue" />
              <Bar dataKey="profit" fill="#10b981" radius={[10, 10, 0, 0]} name="Net Profit" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function CRMDashboard({ users = [], orders = [] }: { users: any[], orders: any[] }) {
  const [searchTerm, setSearchTerm] = useState('');

  const customerStats = (users || []).map(user => {
    const userOrders = (orders || []).filter(o => o.userId === user.id);
    const totalOrders = userOrders.length;
    const totalSpent = userOrders.reduce((acc, o) => acc + (Number(o.totalAmount) || 0), 0);
    
    const lastOrder = userOrders[0];
    const address = (user.savedAddresses && Array.isArray(user.savedAddresses) && user.savedAddresses.length > 0) 
      ? user.savedAddresses[0] 
      : (lastOrder?.customerAddress || 'N/A');
    
    return {
      ...user,
      totalOrders,
      totalSpent,
      address,
      lastOrderDate: lastOrder?.createdAt
    };
  });

  const filteredCustomers = customerStats.filter(c => {
    const name = String(c.displayName || '').toLowerCase();
    const email = String(c.email || '').toLowerCase();
    const phone = String(c.phoneNumber || '');
    const search = searchTerm.toLowerCase();
    return name.includes(search) || email.includes(search) || phone.includes(search);
  });

  const handleExportCSV = () => {
    let csv = "Name,Email,Phone,Address,Created At,Total Orders,Total Spent\n";
    filteredCustomers.forEach(c => {
      const dateObj = c.createdAt?.toDate ? c.createdAt.toDate() : (c.createdAt ? new Date(c.createdAt) : null);
      const createdAt = dateObj && !isNaN(dateObj.getTime()) ? format(dateObj, 'yyyy-MM-dd') : 'N/A';
      csv += `"${String(c.displayName || 'N/A')}","${String(c.email || 'N/A')}","${String(c.phoneNumber || 'N/A')}","${String(c.address || '').replace(/"/g, '""')}","${createdAt}",${c.totalOrders},${c.totalSpent}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `CRM_Customer_Data_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFormattedDate = (date: any) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return 'N/A';
    return format(d, 'MMM dd, yyyy');
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 border border-spice-border shadow-sm">
        <div>
          <h2 className="text-2xl font-serif font-bold text-black">Customer Relationship Management</h2>
          <p className="text-xs text-spice-muted uppercase tracking-widest font-bold mt-1">Manage and analyze your customer base</p>
        </div>
        <button 
          onClick={handleExportCSV}
          className="bg-blue-600 text-white px-6 py-3 text-[10px] uppercase font-bold tracking-widest hover:bg-blue-700 transition-all flex items-center gap-2 shadow-lg"
        >
          <Download className="w-4 h-4" />
          Export Customer Data (CSV)
        </button>
      </div>

      <div className="bg-white border border-spice-border p-4 shadow-sm">
        <div className="relative">
          <input 
            type="text" 
            placeholder="Search by name, email or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-200 focus:border-blue-600 outline-none text-xs font-bold uppercase tracking-widest text-black shadow-sm rounded-xl"
          />
          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spice-muted" />
        </div>
      </div>

      <div className="bg-white border border-spice-border overflow-x-auto shadow-sm">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-spice-light border-b border-spice-border">
              <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-spice-muted">Customer</th>
              <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-spice-muted">Contact Info</th>
              <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-spice-muted">Default Address</th>
              <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-spice-muted">Orders</th>
              <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-spice-muted">Total Spent</th>
              <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-spice-muted">Created Date</th>
            </tr>
          </thead>
          <tbody>
            {(filteredCustomers || []).map((customer, idx) => (
              <tr key={customer.id || idx} className="border-b border-spice-light last:border-0 hover:bg-spice-surface transition-colors">
                <td className="p-4">
                  <div className="font-bold text-black capitalize">{String(customer.displayName || 'Unnamed User')}</div>
                  <div className="text-[10px] text-spice-muted font-mono mt-0.5">{customer.id}</div>
                </td>
                <td className="p-4">
                  <div className="text-sm font-bold text-black">{String(customer.email || 'No Email')}</div>
                  <div className="text-xs text-spice-muted mt-1">{String(customer.phoneNumber || 'No Phone')}</div>
                </td>
                <td className="p-4">
                  <div className="text-xs text-spice-muted max-w-[200px] line-clamp-2" title={String(customer.address || '')}>
                    {String(customer.address || 'N/A')}
                  </div>
                </td>
                <td className="p-4">
                  <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-spice-light text-spice-dark">
                    {customer.totalOrders || 0} Orders
                  </div>
                </td>
                <td className="p-4">
                  <div className="font-bold text-black">₹{(customer.totalSpent || 0).toLocaleString()}</div>
                </td>
                <td className="p-4">
                  <div className="text-xs text-spice-muted">
                    {getFormattedDate(customer.createdAt)}
                  </div>
                </td>
              </tr>
            ))}
            {(!filteredCustomers || filteredCustomers.length === 0) && (
              <tr>
                <td colSpan={6} className="p-12 text-center text-spice-muted uppercase font-bold text-[10px] tracking-widest">
                  No customers found matching your criteria
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, isAdmin, loading, signInWithGoogle, logout, signInWithEmail, signUpWithEmail } = useAuth();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState('manjunathas788@gmail.com');
  const [adminPassword, setAdminPassword] = useState('');
  const [loginTab, setLoginTab] = useState<'google' | 'email'>('google');
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [facilityCenters, setFacilityCenters] = useState<any[]>([]);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [staffForm, setStaffForm] = useState({
    name: '',
    email: '',
    phone: '',
    facilityCenterId: '',
  });
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
  const [inventory, setInventory] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState<'orders' | 'products' | 'categories' | 'category-mgmt' | 'coupons' | 'analytics' | 'facilities' | 'staff' | 'reviews' | 'banners' | 'autorouting' | 'crm'>('orders');
  const [users, setUsers] = useState<any[]>([]);
  const [orderFilter, setOrderFilter] = useState<'all' | 'unassigned'>('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [selectedQrProduct, setSelectedQrProduct] = useState<any>(null);
  const [selectedQrStaff, setSelectedQrStaff] = useState<any>(null);
  const [routingUnitId, setRoutingUnitId] = useState("");
  const [bulkImportProgress, setBulkImportProgress] = useState<{current: number, total: number} | null>(null);

  const exportToCSV = () => {
    if (dbProducts.length === 0) {
      toast.error('No products to export');
      return;
    }

    const headers = ['id', 'name', 'description', 'category', 'price', 'pricePerGram', 'unit', 'stock', 'gstPercentage', 'hsnCode', 'order', 'image', 'speciality', 'season', 'source'];
    const rows = dbProducts.map(p => {
      // Find total stock from inventory
      const productInventory = inventory.filter(inv => inv.productId === p.id);
      const totalStock = productInventory.reduce((sum, inv) => sum + (inv.stock || 0), 0);
      
      return [
        p.id,
        p.name,
        p.description || '',
        p.category,
        p.price,
        p.pricePerGram || 0,
        p.unit || 'g',
        totalStock,
        p.gstPercentage || 5,
        p.hsnCode || '',
        p.order || 0,
        p.image,
        p.speciality || '',
        p.season || '',
        p.source || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => {
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `spice_products_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Products exported successfully');
  };

  const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      
      const productsToImport = lines.slice(1).filter(line => line.trim()).map(line => {
        // Simple CSV parser that handles quotes
        const values: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          if (line[i] === '"') {
            inQuotes = !inQuotes;
          } else if (line[i] === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += line[i];
          }
        }
        values.push(current.trim());

        const prod: any = {};
        headers.forEach((h, idx) => {
          prod[h] = values[idx];
        });
        return prod;
      });

      setBulkImportProgress({ current: 0, total: productsToImport.length });
      
      const chunkSize = 50; // To stay well within the 500 operations per batch limit (1 product + N facility centers)
      
      for (let i = 0; i < productsToImport.length; i += chunkSize) {
        const batch = writeBatch(db);
        const currentChunk = productsToImport.slice(i, i + chunkSize);
        
        for (let j = 0; j < currentChunk.length; j++) {
          const item = currentChunk[j];
          const globalIdx = i + j;
          const productId = item.id && item.id !== 'undefined' ? item.id : `PROD_${Math.random().toString(36).substr(2, 9)}`;
          
          const productData = {
            name: item.name,
            description: item.description,
            category: item.category,
            price: Number(item.price || 0),
            pricePerGram: Number(item.pricePerGram || 0),
            unit: item.unit || 'g',
            gstPercentage: Number(item.gstPercentage || 5),
            hsnCode: item.hsnCode || '',
            order: Number(item.order || 0),
            image: item.image || 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&q=80',
            speciality: item.speciality || '',
            season: item.season || '',
            source: item.source || '',
            variants: [{ 
              weight: item.unit === 'kg' ? '1kg' : '100g', 
              price: Number(item.price || 0), 
              costPrice: Number(item.price || 0) * 0.7 
            }],
            updatedAt: serverTimestamp()
          };

          batch.set(doc(db, 'products', productId), productData, { merge: true });
          
          if (item.stock && facilityCenters.length > 0) {
            const stockPerCenter = Math.floor(Number(item.stock) / facilityCenters.length);
            for (const center of facilityCenters) {
              const invId = `${productId}_${center.id}`;
              batch.set(doc(db, 'inventory', invId), {
                productId,
                facilityCenterId: center.id,
                stock: stockPerCenter,
                updatedAt: serverTimestamp()
              }, { merge: true });
            }
          }
        }

        try {
          await batch.commit();
          setBulkImportProgress(prev => prev ? { ...prev, current: Math.min(i + chunkSize, productsToImport.length) } : null);
        } catch (error) {
          console.error('Batch commit failed:', error);
          toast.error('Failed to import some products');
        }
      }

      toast.success(`Imported ${productsToImport.length} products successfully`);
      setBulkImportProgress(null);
      if (e.target) e.target.value = '';
    };

    reader.readAsText(file);
  };

  const processImageFile = (file: File, callback: (url: string) => void) => {
    const toastId = toast.loading('Processing image...');
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Max dimension 1024px for reasonable base64 size
        const MAX_WIDTH = 1024;
        const MAX_HEIGHT = 1024;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        // Quality 0.7 to stay under Firestore's 1MB limit easily
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        
        callback(dataUrl);
        toast.success('Image optimized and uploaded', { id: toastId });
      };
      img.onerror = () => {
        toast.error('Failed to process image', { id: toastId });
      };
      img.src = event.target?.result as string;
    };
    
    reader.onerror = () => {
      toast.error('Failed to read image file', { id: toastId });
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, callback: (url: string) => void) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImageFile(file, callback);
    if (e.target) e.target.value = '';
  };

  const handleToggleFacility = async (id: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'facilityCenters', id), { isActive: !currentStatus });
      toast.success(`Facility ${!currentStatus ? 'activated' : 'suspended'}`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to toggle facility status');
    }
  };

  const handleCreateFacility = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFacility.name || !newFacility.code || !newFacility.pincode) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const facilityData = {
        name: newFacility.name,
        code: newFacility.code.toUpperCase(),
        pincode: newFacility.pincode,
        pincodes: [newFacility.pincode], // Support legacy array-based routing
        isActive: true,
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'facilityCenters'), facilityData);
      toast.success('Facility center deployed');
      setNewFacility({ name: '', code: '', location: '', pincode: '', pincodes: '' });
    } catch (e) {
      console.error(e);
      toast.error('Failed to deploy facility');
    }
  };

  const handleStaffSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staffForm.name || !staffForm.phone || !staffForm.email) {
      toast.error('Name, Email and Phone are required');
      return;
    }
    try {
      const staffData = {
        name: staffForm.name,
        email: staffForm.email.toLowerCase().trim(),
        phone: staffForm.phone,
        facilityCenterId: staffForm.facilityCenterId,
        updatedAt: serverTimestamp()
      };
      if (editingStaffId) {
        await updateDoc(doc(db, 'staff', editingStaffId), staffData);
        toast.success('Staff record updated');
      } else {
        await addDoc(collection(db, 'staff'), {
          ...staffData,
          createdAt: serverTimestamp()
        });
        toast.success('Staff enlisted');
      }
      setEditingStaffId(null);
      setStaffForm({ name: '', email: '', phone: '', facilityCenterId: '' });
    } catch (error) {
      console.error(error);
      toast.error('Failed to save staff');
    }
  };

  const handleDeleteCoupon = async (code: string) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await deleteDoc(doc(db, 'coupons', code));
      toast.success('Coupon deleted');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete coupon');
    }
  };

  const transformDriveLink = (url: string) => {
    const driveRegex = /\/file\/d\/([a-zA-Z0-9_-]+)/;
    const match = url.match(driveRegex);
    if (match && match[1]) {
      return `https://drive.google.com/uc?export=view&id=${match[1]}`;
    }
    return url;
  };

  // New Facility state
  const [newFacility, setNewFacility] = useState({
    name: '',
    code: '',
    location: '',
    pincode: '',
    pincodes: '',
  });
  const [editingFacility, setEditingFacility] = useState<any>(null);

  // New Coupon state
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    type: 'percent',
    value: '',
    isSingleUse: false,
  });

  // Products state
  const { products: dbProducts, loading: loadingProducts } = useProducts();
  const { categories, loading: loadingCategories } = useCategories();
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    image: '',
    isActive: true,
    order: '0'
  });

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryForm.name) return;

    const toastId = toast.loading(editingCategory ? 'Updating category...' : 'Creating category...');
    try {
      const categoryData: any = {
        name: (categoryForm.name || '').trim(),
        description: (categoryForm.description || '').trim(),
        image: categoryForm.image || '',
        isActive: Boolean(categoryForm.isActive),
        order: Number(categoryForm.order || 0),
        updatedAt: serverTimestamp()
      };

      if (editingCategory) {
        await updateDoc(doc(db, 'categories', editingCategory.id), categoryData);
        toast.success(`Category "${categoryData.name}" updated!`, { id: toastId });
      } else {
        const id = categoryForm.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        await setDoc(doc(db, 'categories', id), {
          ...categoryData,
          createdAt: serverTimestamp()
        });
        toast.success(`Category "${categoryData.name}" created!`, { id: toastId });
      }

      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', image: '', isActive: true, order: '0' });
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || 'Failed to save category', { id: toastId });
    }
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      description: category.description || '',
      image: category.image || '',
      isActive: category.isActive !== undefined ? category.isActive : true,
      order: String(category.order || 0)
    });
    setActiveTab('categories');
    toast.success(`Editing category "${category.name}".`);
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category? Products in this category will become uncategorized.')) return;
    try {
      await deleteDoc(doc(db, 'categories', id));
      toast.success('Category deleted');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete category');
    }
  };

  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    image: '',
    customImage: '',
    category: 'powder',
    variants: [{ weight: '1 Unit', price: 0, costPrice: 0, image: '', stock: 0 }],
    stock: '',
    unit: 'Units',
    pricePerGram: '',
    gstPercentage: '5',
    hsnCode: '',
    order: '0',
    speciality: '',
    season: '',
    source: '',
    isOutOfStock: false,
    unitStocks: {} as Record<string, string>
  });

  useEffect(() => {
    if (!isAdmin) return;

    const qOrders = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
    const unsubOrders = onSnapshot(qOrders, (snapshot) => {
      setOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'orders');
    });

    const qCoupons = query(collection(db, 'coupons'), orderBy('createdAt', 'desc'));
    const unsubCoupons = onSnapshot(qCoupons, (snapshot) => {
      setCoupons(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'coupons');
    });

    const qFacilities = query(collection(db, 'facilityCenters'), orderBy('createdAt', 'desc'));
    const unsubFacilities = onSnapshot(qFacilities, (snapshot) => {
      setFacilityCenters(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'facilityCenters');
    });

    const qStaff = query(collection(db, 'staff'), orderBy('createdAt', 'desc'));
    const unsubStaff = onSnapshot(qStaff, (snapshot) => {
      setStaffList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'staff');
    });

    const qInventory = query(collection(db, 'inventory'));
    const unsubInventory = onSnapshot(qInventory, (snapshot) => {
      setInventory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'inventory');
    });

    const qNotifications = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
    let initialLoad = true;
    const unsubNotifications = onSnapshot(qNotifications, (snapshot) => {
      const newNotifications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // If not initial load and there are new unread notifications compared to current state
      if (!initialLoad) {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const data = change.doc.data();
            if (!data.read) {
              toast(data.title + ': ' + data.message, {
                icon: '🔔',
                duration: 5000
              });
            }
          }
        });
      }
      
      setNotifications(newNotifications);
      initialLoad = false;
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'notifications');
    });

    const qReviews = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'));
    const unsubReviews = onSnapshot(qReviews, (snapshot) => {
      setReviews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'reviews');
    });

    const qUsers = collection(db, 'users');
    const unsubUsers = onSnapshot(qUsers, (snapshot) => {
      setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'users');
    });

    return () => {
      unsubOrders();
      unsubCoupons();
      unsubFacilities();
      unsubStaff();
      unsubInventory();
      unsubNotifications();
      unsubReviews();
      unsubUsers();
    };
  }, [isAdmin]);

  const handleAutoAssignAll = async () => {
    const unassigned = orders.filter(o => !o.facilityCenterId);
    if (unassigned.length === 0) {
      toast.success('No unassigned orders found');
      return;
    }

    let assignedCount = 0;
    toast.loading('Routing orders...', { id: 'auto-assign' });

    for (const order of unassigned) {
      const matchedFacility = facilityCenters.find(f => 
        f.isActive && f.pincodes && Array.isArray(f.pincodes) && f.pincodes.includes(order.pincode)
      );

      if (matchedFacility) {
        try {
          await updateDoc(doc(db, 'orders', order.id), { facilityCenterId: matchedFacility.id });
          await createNotification(
            'Order Auto-Routed',
            `Order #${order.orderNumber || order.id.slice(0,8)} automatically routed to: ${matchedFacility.name}`,
            'facility',
            order.id
          );
          assignedCount++;
        } catch (e) {
          console.error(`Failed to assign order ${order.id}`, e);
        }
      }
    }

    toast.dismiss('auto-assign');
    if (assignedCount > 0) {
      toast.success(`Successfully assigned ${assignedCount} orders`);
    } else {
      toast.error('No matches found for unassigned orders');
    }
  };

  const handleUpdateOrderStatus = async (orderId: string, status: string) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), { status });
      
      const order = orders.find(o => o.id === orderId);
      await createNotification(
        'Order Status Updated',
        `Order #${order?.orderNumber || orderId.slice(0,8)} is now: ${status}`,
        'status',
        orderId
      );
    } catch (e) {
      console.error(e);
      alert('Failed to update status');
    }
  };

  const handleManualAssign = async (orderId: string, facilityId: string) => {
    if (!facilityId) return;
    const unit = facilityCenters.find(f => f.id === facilityId);
    try {
      toast.loading(`Assigning order to ${unit?.name || 'unit'}...`, { id: 'manual-assign' });
      await updateDoc(doc(db, 'orders', orderId), { facilityCenterId: facilityId });
      await createNotification(
        'Order Manually Assigned',
        `Order manually routed to: ${unit?.name}`,
        'facility',
        orderId
      );
      toast.success(`Successfully assigned to ${unit?.name}`, { id: 'manual-assign' });
    } catch (e) {
      console.error(e);
      toast.error('Failed to assign order', { id: 'manual-assign' });
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    if (window.confirm('Are you sure you want to permanently delete this order record?')) {
      try {
        await deleteDoc(doc(db, 'orders', orderId));
      } catch (e) {
        console.error(e);
        alert('Failed to delete order');
      }
    }
  };

  const handleCreateCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCoupon.code || !newCoupon.value) return;

    try {
      const couponCode = newCoupon.code.toUpperCase();
      await setDoc(doc(db, 'coupons', couponCode), {
        code: couponCode,
        type: newCoupon.type,
        value: Number(newCoupon.value),
        isSingleUse: newCoupon.isSingleUse,
        isActive: true,
        createdAt: serverTimestamp(),
      });
      setNewCoupon({ code: '', type: 'percent', value: '', isSingleUse: false });
    } catch (e) {
      console.error(e);
      alert('Failed to create coupon');
    }
  };

  const handleToggleCoupon = async (couponId: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'coupons', couponId), { isActive: !currentStatus });
    } catch (e) {
      console.error(e);
      alert('Failed to update coupon');
    }
  };

  const handlePrintLabel = (order: any) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const displayOrderId = order.orderNumber || order.id.slice(0, 8);

    const html = `
      <html>
        <head>
          <title>Shipping Label - ${displayOrderId}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; color: #000; }
            .label { border: 2px solid #000; width: 4in; height: 6in; padding: 20px; box-sizing: border-box; display: flex; flex-direction: column; }
            .header { border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 15px; font-weight: bold; font-size: 24px; text-transform: uppercase; text-align: center; }
            .section { margin-bottom: 15px; }
            .section-title { font-size: 10px; color: #666; text-transform: uppercase; margin-bottom: 5px; font-weight: bold; }
            .text { font-size: 14px; margin: 0; line-height: 1.4; }
            .order-id-display { font-size: 18px; font-weight: bold; margin-top: 5px; }
            .qr-container { display: flex; justify-content: center; align-items: center; margin-top: auto; padding-top: 15px; border-top: 1px dashed #ccc; }
            .qr-code { width: 120px; height: 120px; }
            .footer { border-top: 2px solid #000; padding-top: 10px; margin-top: 15px; font-size: 11px; text-align: center; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="label">
            <div class="header">AJJAS MASALA</div>
            <div class="section">
              <div class="section-title">Ship To</div>
              <p class="text"><strong>${order.customerName}</strong></p>
              <p class="text">${order.customerAddress.replace(/\n/g, '<br>')}</p>
              <p class="text">Phone: ${order.customerPhone}</p>
            </div>
            <div class="section">
              <div class="section-title">Order Details</div>
              <p class="text order-id-display">Order ID: #${order.orderNumber || order.id}</p>
              <p class="text">Date: ${order.createdAt?.toDate ? new Date(order.createdAt.toDate()).toLocaleDateString() : new Date(order.createdAt || Date.now()).toLocaleDateString()}</p>
              <p class="text">Type: ${order.businessType}</p>
              ${order.gstNumber ? `<p class="text">GST: ${order.gstNumber}</p>` : ''}
              <p class="text">Payment: ${order.paymentMethod}</p>
            </div>
            <div class="qr-container">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${order.id}" class="qr-code" alt="QR Code" />
            </div>
            <div class="footer">
              Thank you for choosing AJJAS FOOD PRODUCTS
            </div>
          </div>
          <script>
            window.onload = () => {
              setTimeout(() => {
                window.print();
              }, 500);
            }
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handlePrintInvoice = (order: any) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <html>
        <head>
          <title>Invoice - ${order.id}</title>
          <style>
            body { font-family: sans-serif; padding: 40px; color: #333; }
            .header { display: flex; justify-content: space-between; border-bottom: 2px solid #e2e8f0; padding-bottom: 20px; margin-bottom: 30px; }
            .company-name { font-size: 28px; font-weight: bold; color: #b91c1c; text-transform: uppercase; }
            .invoice-title { font-size: 24px; color: #64748b; }
            .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 40px; }
            .section-title { font-size: 12px; font-weight: bold; color: #94a3b8; text-transform: uppercase; margin-bottom: 10px; letter-spacing: 1px; }
            .text { margin: 0 0 5px 0; font-size: 14px; line-height: 1.5; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            th { text-align: left; padding: 12px; border-bottom: 2px solid #e2e8f0; font-size: 12px; color: #64748b; text-transform: uppercase; }
            td { padding: 12px; border-bottom: 1px solid #f1f5f9; font-size: 14px; }
            .text-right { text-align: right; }
            .totals { width: 300px; margin-left: auto; }
            .total-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f1f5f9; }
            .total-row.final { font-weight: bold; font-size: 18px; border-bottom: none; border-top: 2px solid #e2e8f0; margin-top: 10px; padding-top: 15px; }
            .footer { margin-top: 50px; text-align: center; color: #94a3b8; font-size: 12px; padding-top: 20px; border-top: 1px solid #e2e8f0; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="company-name">Ajjas Masala</div>
              <div class="text" style="margin-top: 10px;">
                Ajjas food products, Industrial area, Ganjam, <br>
                Srirangapatna Taluk, Mandya District-571477
              </div>
              <div class="text">enquiries@ajjasfoods.shop</div>
              <div class="text">GSTIN: 29ABCDE1234F1Z5</div>
            </div>
            <div class="text-right">
              <div class="invoice-title">INVOICE</div>
              <div class="text" style="margin-top: 10px;"><strong>Order ID:</strong> ${order.orderNumber || order.id}</div>
              <div class="text"><strong>Date:</strong> ${order.createdAt?.toDate ? new Date(order.createdAt.toDate()).toLocaleDateString() : new Date(order.createdAt || Date.now()).toLocaleDateString()}</div>
              <div class="text"><strong>Payment:</strong> ${order.paymentMethod}</div>
            </div>
          </div>
          
          <div class="details-grid">
            <div>
              <div class="section-title">Billed To</div>
              <div class="text"><strong>${order.customerName}</strong></div>
              <div class="text">${order.customerAddress.replace(/\n/g, '<br>')}</div>
              <div class="text">Phone: ${order.customerPhone}</div>
              ${order.gstNumber ? `<div class="text">GST: ${order.gstNumber}</div>` : ''}
              <div class="text">Type: ${order.businessType}</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Weight</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Price</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${order.items.map((i: any) => `
                <tr>
                  <td><strong>${i.name}</strong></td>
                  <td>${i.selectedWeight}</td>
                  <td class="text-right">${i.quantity}</td>
                  <td class="text-right">₹${i.price}</td>
                  <td class="text-right">₹${i.price * i.quantity}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="totals">
            <div class="total-row">
              <span>Taxable Value</span>
              <span>₹${(order.totalAmount - (order.gstAmount || 0)).toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>GST Total</span>
              <span>₹${(order.gstAmount || 0).toFixed(2)}</span>
            </div>
            ${order.discountAmount > 0 ? `
            <div class="total-row" style="color: #b91c1c;">
              <span>Discount (${order.couponCode})</span>
              <span>-₹${order.discountAmount}</span>
            </div>
            ` : ''}
            <div class="total-row final">
              <span>Grand Total (Incl. Tax)</span>
              <span>₹${order.totalAmount}</span>
            </div>
          </div>

          <div style="margin-top: 20px; font-size: 10px; color: #666; border-top: 1px dashed #eee; padding-top: 10px;">
            <p><strong>Tax Invoice Details (GST Breakdown):</strong></p>
            <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
              <thead>
                <tr style="text-align: left; border-bottom: 1px solid #eee;">
                  <th style="padding: 4px 0;">Product</th>
                  <th style="padding: 4px 0;">HSN/SAC</th>
                  <th style="padding: 4px 0;">Taxable Val</th>
                  <th style="padding: 4px 0;">GST %</th>
                  <th style="padding: 4px 0; text-align: right;">CGST</th>
                  <th style="padding: 4px 0; text-align: right;">SGST</th>
                  <th style="padding: 4px 0; text-align: right;">Total Tax</th>
                </tr>
              </thead>
              <tbody>
                ${order.items.map((item: any) => {
                  const itemTotal = item.price * item.quantity;
                  const rate = item.gstPercentage || 5;
                  const taxable = itemTotal / (1 + rate/100);
                  const tax = itemTotal - taxable;
                  const halfTax = (tax / 2).toFixed(2);
                  return `
                    <tr>
                      <td style="padding: 4px 0;">${item.name}</td>
                      <td style="padding: 4px 0;">${item.hsnCode || '-'}</td>
                      <td style="padding: 4px 0;">₹${taxable.toFixed(2)}</td>
                      <td style="padding: 4px 0;">${rate}%</td>
                      <td style="padding: 4px 0; text-align: right;">₹${halfTax}</td>
                      <td style="padding: 4px 0; text-align: right;">₹${halfTax}</td>
                      <td style="padding: 4px 0; text-align: right;">₹${tax.toFixed(2)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
          
          <div class="footer">
            Thank you for shopping with Ajjas Masala!<br>
            This is a computer-generated invoice and doesn't require a signature.
          </div>
          <script>
            window.onload = () => { window.print(); }
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleExportGstReport = () => {
    const successfulOrders = orders.filter(o => o.status !== 'Returned');
    
    // CSV Header
    let csv = "Date,Order ID,Customer,GSTIN,Taxable Value,GST %,CGST,SGST,IGST,Total Tax,Grand Total\n";
    
    successfulOrders.forEach(order => {
      const date = order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString() : new Date(order.createdAt || Date.now()).toLocaleDateString();
      const orderId = order.orderNumber || order.id;
      const customer = order.customerName;
      const gstin = order.gstNumber || 'Unregistered';
      
      (order.items || []).forEach((item: any) => {
        const itemTotal = item.price * item.quantity;
        const rate = item.gstPercentage || 5;
        const taxable = itemTotal / (1 + rate/100);
        const tax = itemTotal - taxable;
        const halfTax = (tax / 2).toFixed(2);
        
        // Simplified GST logic: Assuming intra-state (CGST+SGST) for demo purposes
        // In real app, check state of origin vs state of destination
        csv += `${date},${orderId},${customer},${gstin},${taxable.toFixed(2)},${rate}%,${halfTax},${halfTax},0,${tax.toFixed(2)},${itemTotal}\n`;
      });
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `GST_Report_${format(new Date(), 'MMM_yyyy')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') return;
      console.error('Login error:', error);
      alert(`Login failed. If you are in the preview environment, please open the app in a new tab/window to log in. Error: ${error.message}`);
    }
  };

  const handleSeedSampleProducts = async () => {
    if (!isAdmin) return;
    
    // Check if we also need to seed categories
    if (categories.length === 0) {
      if (window.confirm('No categories found. Would you like to seed experimental categories first?')) {
        const sampleCats = [
          { name: 'Spices & Powders', id: 'powder', order: 1, isActive: true, description: 'Pure ground spices' },
          { name: 'Authentic Masalas', id: 'masala', order: 2, isActive: true, description: 'Traditional blended masalas' },
          { name: 'Whole Spices', id: 'whole', order: 3, isActive: true, description: 'Fresh whole spice seeds' },
          { name: 'Gourmet Blends', id: 'blends', order: 4, isActive: true, description: 'Special tea and snack blends' },
          { name: 'Cold Pressed Oils', id: 'oils', order: 5, isActive: true, description: 'Traditional wood pressed oils' }
        ];
        
        try {
          for (const cat of sampleCats) {
            const { id, ...data } = cat;
            await setDoc(doc(db, 'categories', id), { ...data, createdAt: serverTimestamp() });
          }
          toast.success('Categories seeded successfully');
        } catch (err) {
          toast.error('Failed to seed categories');
          return;
        }
      }
    }

    if (dbProducts.length > 0) {
      if (!window.confirm('Products already exist. Do you want to add more sample products anyway?')) return;
    }

    const sampleProducts = [
      // POWDER CATEGORY (4 Products)
      {
        name: "Premium Turmeric Powder",
        description: "Pure Salem turmeric with high curcumin content. Adds vibrant color and earthy flavor to your dishes.",
        price: 65,
        category: "powder",
        image: "https://images.unsplash.com/photo-1615485290382-441e4d019cb0?auto=format&fit=crop&q=80&w=800",
        unit: "Units",
        hsnCode: "0910",
        order: 1,
        variants: [
          { weight: "1 Unit", price: 65, costPrice: 40 },
          { weight: "2 Units", price: 150, costPrice: 90 }
        ],
        speciality: "Curcumin 5%+",
        season: "Peak Harvest",
        source: "Salem, TN"
      },
      {
        name: "Guntur Red Chilli Powder",
        description: "Extremely spicy and perfectly ground red chillies from Guntur. Perfect for Indian curries.",
        price: 55,
        category: "powder",
        image: "https://images.unsplash.com/photo-1599481238505-b8b0537a3f77?auto=format&fit=crop&q=80&w=800",
        unit: "Units",
        hsnCode: "0904",
        order: 2,
        variants: [
          { weight: "1 Unit", price: 55, costPrice: 35 },
          { weight: "2 Units", price: 130, costPrice: 80 }
        ],
        speciality: "High Heat",
        season: "Oct - Feb",
        source: "Guntur, AP"
      },
      {
        name: "Rajasthan Coriander Powder",
        description: "Freshly ground aromatic coriander seeds from Rajasthan. Essential for every Indian kitchen.",
        price: 45,
        category: "powder",
        image: "https://images.unsplash.com/photo-1615485499763-c373cb7f9b88?auto=format&fit=crop&q=80&w=800",
        unit: "Units",
        hsnCode: "0909",
        order: 3,
        variants: [
          { weight: "1 Unit", price: 45, costPrice: 25 },
          { weight: "2 Units", price: 105, costPrice: 60 }
        ],
        speciality: "Stone Ground",
        season: "Winter Crop",
        source: "Kota, RJ"
      },
      {
        name: "Kashmiri Chilly Powder",
        description: "Mild heat with deep red color. Perfect for dishes that need color without extreme spice.",
        price: 95,
        category: "powder",
        image: "https://images.unsplash.com/photo-1601648764658-cf37e8c89b70?auto=format&fit=crop&q=80&w=800",
        unit: "Units",
        hsnCode: "0904",
        order: 4,
        variants: [
          { weight: "1 Unit", price: 95, costPrice: 60 }
        ],
        speciality: "Mild & Vibrant",
        season: "Winter",
        source: "Kashmir, JK"
      },
      
      // MASALA CATEGORY (4 Products)
      {
        name: "Premium Biryani Masala",
        description: "A secret royal blend of 18 whole spices to make the perfect aromatic biryani.",
        price: 85,
        category: "masala",
        image: "https://images.unsplash.com/photo-1596450514735-24402d44335c?auto=format&fit=crop&q=80&w=800",
        unit: "Units",
        hsnCode: "0910",
        order: 5,
        variants: [
          { weight: "1 Unit", price: 85, costPrice: 50 },
          { weight: "2 Units", price: 160, costPrice: 95 }
        ],
        speciality: "Secret Blend",
        season: "Year Round",
        source: "Mughalai"
      },
      {
        name: "Kitchen King Garam Masala",
        description: "The ultimate spice blend for vegetarian and non-vegetarian curries. Deeply aromatic.",
        price: 75,
        category: "masala",
        image: "https://images.unsplash.com/photo-1532336414038-cf19250c5757?auto=format&fit=crop&q=80&w=800",
        unit: "Units",
        hsnCode: "0910",
        order: 6,
        variants: [{ weight: "1 Unit", price: 75, costPrice: 45 }],
        speciality: "All-Purpose",
        season: "Year Round",
        source: "North India"
      },
      {
        name: "Classic Sambar Powder",
        description: "Authentic Brahmin-style sambar powder with roasted lentils and selected spices.",
        price: 65,
        category: "masala",
        image: "https://images.unsplash.com/photo-1626132646522-49141049cb42?auto=format&fit=crop&q=80&w=800",
        unit: "Units",
        hsnCode: "0910",
        order: 7,
        variants: [{ weight: "1 Unit", price: 65, costPrice: 40 }],
        speciality: "Southern Traditional",
        season: "Year Round",
        source: "Karnataka"
      },
      {
        name: "Spicy Chicken Masala",
        description: "Robust blend of black pepper and ginger for the perfect chicken fry or curry.",
        price: 70,
        category: "masala",
        image: "https://images.unsplash.com/photo-1606471191009-63994c53403f?auto=format&fit=crop&q=80&w=800",
        unit: "Units",
        hsnCode: "0910",
        order: 8,
        variants: [{ weight: "1 Unit", price: 70, costPrice: 42 }],
        speciality: "Pepper Heavy",
        season: "Year Round",
        source: "Tamil Nadu"
      },

      // WHOLE SPICES (3 Products)
      {
        name: "Whole Jeera (Cumin)",
        description: "Bold and aromatic whole cumin seeds. Perfect for tempering and everyday cooking.",
        price: 70,
        category: "whole",
        image: "https://images.unsplash.com/photo-1615485290333-e18e5ac20473?auto=format&fit=crop&q=80&w=800",
        unit: "g",
        hsnCode: "0909",
        order: 9,
        variants: [
          { weight: "100g", price: 70, costPrice: 45 },
          { weight: "250g", price: 165, costPrice: 100 }
        ],
        speciality: "Bold Seeds",
        season: "Summer",
        source: "Unjha, GJ"
      },
      {
        name: "Malabar Black Pepper",
        description: "King of spices. Bold black peppercorns from the hills of Malabar. Extremely pungent.",
        price: 120,
        category: "whole",
        image: "https://images.unsplash.com/photo-1533230408708-8f9f91d1235a?auto=format&fit=crop&q=80&w=800",
        unit: "g",
        hsnCode: "0904",
        order: 10,
        variants: [{ weight: "100g", price: 120, costPrice: 80 }],
        speciality: "Organic Grade",
        season: "Harvest Cycle",
        source: "Wayanad, KL"
      },
      {
        name: "Green Cardamom (8mm)",
        description: "Exquisite 8mm bold green cardamom pods. High oil content and superior aroma.",
        price: 350,
        category: "whole",
        image: "https://images.unsplash.com/photo-1599488615731-7e5c2823ff28?auto=format&fit=crop&q=80&w=800",
        unit: "g",
        hsnCode: "0908",
        order: 11,
        variants: [{ weight: "50g", price: 350, costPrice: 240 }],
        speciality: "Export Quality",
        season: "Fresh Crop",
        source: "Idukki, KL"
      },

      // BLENDS (3 Products)
      {
        name: "Ginger Tea Masala",
        description: "Warming blend of dried ginger, cardamom, and black pepper for your morning tea.",
        price: 80,
        category: "blends",
        image: "https://images.unsplash.com/photo-1544787210-2827443cb69b?auto=format&fit=crop&q=80&w=800",
        unit: "g",
        hsnCode: "0910",
        order: 12,
        variants: [{ weight: "50g", price: 80, costPrice: 50 }],
        speciality: "Immunity Boost",
        source: "Heritage Blend"
      },
      {
        name: "Zesty Chat Masala",
        description: "Tangy and spicy blend perfect for fruits, snacks, and street food at home.",
        price: 55,
        category: "blends",
        image: "https://images.unsplash.com/photo-1514336020551-7c9b2cc5555d?auto=format&fit=crop&q=80&w=800",
        unit: "g",
        hsnCode: "0910",
        order: 13,
        variants: [{ weight: "100g", price: 55, costPrice: 30 }]
      },
      {
        name: "Sandwich Masala",
        description: "The secret ingredient to Mumbai's famous street sandwiches. Tangy and spicy.",
        price: 45,
        category: "blends",
        image: "https://images.unsplash.com/photo-1596450514735-24402d44335c?auto=format&fit=crop&q=80&w=800",
        unit: "g",
        hsnCode: "0910",
        order: 14,
        variants: [{ weight: "100g", price: 45, costPrice: 25 }]
      },

      // OILS (3 Products)
      {
        name: "Wood Pressed Coconut Oil",
        description: "100% pure cold pressed coconut oil, extracted using traditional wooden ghani.",
        price: 320,
        category: "oils",
        image: "https://images.unsplash.com/photo-1620980590343-4f9e4f513904?auto=format&fit=crop&q=80&w=800",
        unit: "ml",
        hsnCode: "1513",
        order: 15,
        variants: [{ weight: "500ml", price: 320, costPrice: 240 }, { weight: "1L", price: 620, costPrice: 450 }],
        speciality: "Edible Grade",
        source: "Pollachi"
      },
      {
        name: "Pure Gingelly (Sesame) Oil",
        description: "Traditional cold pressed sesame oil with palm jaggery. Rich in nutrients.",
        price: 450,
        category: "oils",
        image: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?auto=format&fit=crop&q=80&w=800",
        unit: "ml",
        hsnCode: "1515",
        order: 16,
        variants: [{ weight: "500ml", price: 450, costPrice: 320 }],
        source: "Tamil Nadu"
      },
      {
        name: "Cold Pressed Groundnut Oil",
        description: "Nutty and aromatic groundnut oil. Low temperature extraction preserves all proteins.",
        price: 280,
        category: "oils",
        image: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?auto=format&fit=crop&q=80&w=800",
        unit: "ml",
        hsnCode: "1508",
        order: 17,
        variants: [{ weight: "1L", price: 280, costPrice: 190 }],
        source: "Saurashtra"
      }
    ];

    try {
      toast.loading('Seeding sample products...', { id: 'seed-products' });
      const batch = writeBatch(db);
      
      for (const pData of sampleProducts) {
        const productRef = doc(collection(db, 'products'));
        batch.set(productRef, {
          ...pData,
          gstPercentage: 5,
          createdAt: serverTimestamp()
        });

        // Add initial inventory if facility centers exist
        if (facilityCenters.length > 0) {
          for (const center of facilityCenters) {
            const invId = `${productRef.id}_${center.id}`;
            batch.set(doc(db, 'inventory', invId), {
              productId: productRef.id,
              facilityCenterId: center.id,
              stock: 5000, // Initial 5kg/5000 units
              updatedAt: serverTimestamp()
            });
          }
        }
      }

      await batch.commit();
      toast.success('Sample products seeded successfully!', { id: 'seed-products' });
    } catch (error) {
      console.error(error);
      toast.error('Failed to seed sample products', { id: 'seed-products' });
    }
  };

  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productForm.name) {
      toast.error('Product name is required');
      return;
    }

    const toastId = toast.loading(editingProduct ? 'Updating product...' : 'Creating product...');
    try {
      const isWholeSpice = productForm.category === 'whole';
      const finalUnit = isWholeSpice ? (productForm.unit || 'g') : 'Units';
      
      const formattedVariants = (productForm.variants || []).map((v, i) => {
        let weight = (v.weight || '').trim();
        if (!isWholeSpice) {
          if (!weight) {
            weight = `${i + 1} Unit${i === 0 ? '' : 's'}`;
          } else if (!/\bunit\b/i.test(weight)) {
            weight = `${weight} Unit${weight === '1' ? '' : 's'}`;
          }
        }
        return {
          weight: weight || `${i + 1} Unit`,
          price: Number(v.price) || 0,
          costPrice: Number(v.costPrice || 0),
          image: v.image || '',
          stock: Number(v.stock || 0)
        };
      });

      const productData: any = {
        name: (productForm.name || '').trim(),
        description: (productForm.description || '').trim(),
        price: Number(productForm.price) || 0,
        image: productForm.image || '',
        customImage: productForm.customImage || '',
        category: productForm.category || 'powder',
        variants: formattedVariants,
        unit: finalUnit,
        pricePerGram: isWholeSpice ? (Number(productForm.pricePerGram) || 0) : 0,
        gstPercentage: Number(productForm.gstPercentage) || 5,
        hsnCode: productForm.hsnCode || '',
        order: Number(productForm.order) || 0,
        speciality: productForm.speciality || '',
        season: productForm.season || '',
        source: productForm.source || '',
        isOutOfStock: Boolean(productForm.isOutOfStock),
        updatedAt: serverTimestamp()
      };

      let productId = editingProduct?.id;
      if (editingProduct) {
        await updateDoc(doc(db, 'products', editingProduct.id), productData);
        toast.success(`Product "${productData.name}" updated successfully!`, { id: toastId });
      } else {
        const docRef = await addDoc(collection(db, 'products'), {
          ...productData,
          createdAt: serverTimestamp()
        });
        productId = docRef.id;
        toast.success(`Product "${productData.name}" added successfully!`, { id: toastId });
      }

      // Handle multi-unit inventory save per variant safely
      if (productForm.unitStocks && Object.keys(productForm.unitStocks).length > 0) {
        try {
          const invBatch = writeBatch(db);
          let invCount = 0;
          for (const compoundKey in productForm.unitStocks) {
            const stockVal = Number(productForm.unitStocks[compoundKey]);
            if (!isNaN(stockVal)) {
              const [vIdxStr, facilityId] = compoundKey.split('_');
              const vIdx = parseInt(vIdxStr, 10);
              const variant = formattedVariants[vIdx];
              if (variant && facilityId) {
                const variantWeight = variant.weight;
                const invId = `${productId}_${variantWeight.replace(/\s+/g, '_')}_${facilityId}`;
                invBatch.set(doc(db, 'inventory', invId), {
                  productId,
                  facilityCenterId: facilityId,
                  variantWeight,
                  stock: stockVal,
                  updatedAt: serverTimestamp()
                }, { merge: true });
                invCount++;
              }
            }
          }
          if (invCount > 0) {
            await invBatch.commit();
          }
        } catch (invErr) {
          console.warn("Facility inventory sync notice:", invErr);
        }
      }

      setEditingProduct(null);
      setProductForm({ 
        name: '', 
        description: '', 
        price: '', 
        image: '', 
        customImage: '',
        category: 'powder', 
        variants: [{ weight: '1 Unit', price: 0, costPrice: 0, image: '', stock: 0 }], 
        stock: '', 
        unit: 'Units',
        pricePerGram: '',
        gstPercentage: '5',
        hsnCode: '',
        order: '0',
        speciality: '',
        season: '',
        source: '',
        isOutOfStock: false,
        unitStocks: {}
      });
    } catch (error: any) {
      console.error("Save product error:", error);
      toast.error(error.message || 'Failed to save product', { id: toastId });
    }
  };

  const handleEditProduct = (prod: any) => {
    setEditingProduct(prod);
    
    // Migration logic for old products added before the variants feature
    let initialVariants = prod.variants || [];
    if (initialVariants.length === 0 && prod.weights && Array.isArray(prod.weights)) {
      initialVariants = prod.weights.map((w: string) => ({ 
        weight: w, 
        price: prod.price || 0 
      }));
    }
    
    // Ensure at least one variant exists for the form
    if (initialVariants.length === 0) {
      initialVariants = [{ weight: prod.category === 'whole' ? '100g' : '1 Unit', price: prod.price || 0 }];
    }

    setProductForm({
      name: prod.name || '',
      description: prod.description || '',
      price: (prod.price || 0).toString(),
      image: prod.image || '',
      customImage: prod.customImage || '',
      category: prod.category || 'powder',
      variants: initialVariants.map((v: any) => ({
        weight: v.weight || '1 Unit',
        price: v.price || 0,
        costPrice: v.costPrice || 0,
        image: v.image || '',
        stock: v.stock || 0
      })),
      stock: prod.stock !== undefined ? prod.stock.toString() : '',
      unit: prod.category === 'whole' ? (prod.unit || 'g') : 'Units',
      pricePerGram: (prod.pricePerGram || 0).toString(),
      gstPercentage: (prod.gstPercentage || 5).toString(),
      hsnCode: prod.hsnCode || '',
      order: (prod.order ?? 0).toString(),
      speciality: prod.speciality || '',
      season: prod.season || '',
      source: prod.source || '',
      isOutOfStock: prod.isOutOfStock || false,
      unitStocks: inventory
        .filter(inv => inv.productId === prod.id)
        .reduce((acc, inv) => {
          // If the inventory document has a variantWeight configured, find its index, or default to 0
          const vIdx = inv.variantWeight
            ? initialVariants.findIndex((v: any) => v.weight === inv.variantWeight)
            : 0;
          if (vIdx !== -1) {
            acc[`${vIdx}_${inv.facilityCenterId}`] = inv.stock.toString();
          }
          return acc;
        }, {})
    });

    setActiveTab('products');

    setTimeout(() => {
      const el = document.getElementById('product-form-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);

    toast.success(`Editing "${prod.name}". Scroll to form above.`);
  };

  const handleAddVariant = () => {
    setProductForm({
      ...productForm,
      variants: [...productForm.variants, { weight: '', price: 0, costPrice: 0, image: '', stock: 0 }]
    });
  };

  const handleRemoveVariant = (index: number) => {
    const newVariants = [...productForm.variants];
    newVariants.splice(index, 1);
    setProductForm({
      ...productForm,
      variants: newVariants
    });
  };

  const handleUpdateVariant = (index: number, field: string, value: any) => {
    const newVariants = [...productForm.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setProductForm({
      ...productForm,
      variants: newVariants
    });
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
       await deleteDoc(doc(db, 'products', id));
    } catch (error) {
       console.error(error);
       alert('Failed to delete product');
    }
  };

  const handleSaveFacility = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFacility.name || newFacility.code.length !== 6) {
      alert('Code must be exactly 6 characters');
      return;
    }

    try {
      const facilityData = {
        name: newFacility.name,
        code: newFacility.code.toUpperCase(),
        location: newFacility.location,
        pincodes: newFacility.pincodes.split(',').map(p => p.trim()).filter(p => p !== ''),
        isActive: true,
        createdAt: serverTimestamp(),
      };

      if (editingFacility) {
        await updateDoc(doc(db, 'facilityCenters', editingFacility.id), {
          ...facilityData,
          createdAt: editingFacility.createdAt // Keep original timestamp
        });
      } else {
        const docRef = await addDoc(collection(db, 'facilityCenters'), facilityData);
        await createNotification(
          'New Facility Created',
          `New unit "${facilityData.name}" has been established.`,
          'facility',
          docRef.id
        );
      }
      
      setNewFacility({ name: '', code: '', location: '', pincode: '', pincodes: '' });
      setEditingFacility(null);
    } catch (e) {
      console.error(e);
      alert('Failed to save facility center');
    }
  };

  const handleDeleteFacility = async (id: string) => {
    if (window.confirm('Delete facility? This might affect assigned staff.')) {
      await deleteDoc(doc(db, 'facilityCenters', id));
    }
  };

  const handleDeleteStaff = async (id: string) => {
    if (window.confirm('Remove staff assignment?')) {
      await deleteDoc(doc(db, 'staff', id));
    }
  };

  const handleUpdateReviewStatus = async (reviewId: string, status: 'published' | 'hidden') => {
    try {
      await updateDoc(doc(db, 'reviews', reviewId), { status });
      toast.success(`Review ${status}`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to update review status');
    }
  };

  const handleDeleteReviewAdmin = async (reviewId: string) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await deleteDoc(doc(db, 'reviews', reviewId));
      toast.success('Review deleted');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete review');
    }
  };

  const [editingReview, setEditingReview] = useState<any>(null);
  const [reviewEditForm, setReviewEditForm] = useState({ rating: 5, comment: '' });
  
  // Banner Settings State
  const [bannerSettings, setBannerSettings] = useState({
    bannerImage: ''
  });
  const [savingBanner, setSavingBanner] = useState(false);

  useEffect(() => {
    // Fetch banner settings on load
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'hero_banner');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setBannerSettings(docSnap.data().value);
        }
      } catch (e) {
        console.error("Failed to fetch banner settings", e);
      }
    };
    if (isAdmin) fetchSettings();
  }, [isAdmin]);

  const handleSaveBanner = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingBanner(true);
    try {
      await setDoc(doc(db, 'settings', 'hero_banner'), {
        key: 'hero_banner',
        value: bannerSettings,
        updatedAt: serverTimestamp()
      });
      toast.success('Banner settings updated successfully!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to save banner settings');
    } finally {
      setSavingBanner(false);
    }
  };

  const handleEditReviewAdmin = (review: any) => {
    setEditingReview(review);
    setReviewEditForm({ rating: review.rating, comment: review.comment });
  };

  const handleSaveReviewAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingReview) return;
    try {
      await updateDoc(doc(db, 'reviews', editingReview.id), {
        rating: reviewEditForm.rating,
        comment: reviewEditForm.comment,
        updatedAt: serverTimestamp()
      });
      setEditingReview(null);
      toast.success('Review updated');
    } catch (e) {
      console.error(e);
      toast.error('Failed to update review');
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center">Loading...</div>;

  if (!user || !isAdmin) {
    const isTargetEmail = (user?.email?.toLowerCase().trim() === 'manjunathas788@gmail.com') || (user?.email?.toLowerCase().trim().startsWith('admin@'));

    const handleElevateCurrentAccount = async () => {
      if (!user) return;
      try {
        toast.loading('Provisioning Admin status in Firestore...', { id: 'elevate' });
        await setDoc(doc(db, 'admins', user.uid), {
          email: user.email,
          role: 'superadmin',
          createdAt: serverTimestamp()
        }, { merge: true });
        toast.success('Admin privileges successfully activated! Reloading...', { id: 'elevate' });
        setTimeout(() => window.location.reload(), 400);
      } catch (err: any) {
        toast.error(err.message || 'Elevation failed', { id: 'elevate' });
      }
    };

    const handleEmailLoginSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setAdminLoginLoading(true);
      try {
        await signInWithEmail(adminEmail, adminPassword);
        toast.success('Logged in successfully!');
      } catch (err: any) {
        console.error("Admin login error:", err);
        if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
          try {
            await signUpWithEmail(adminEmail, adminPassword, 'Admin High Archivist');
            toast.success('Admin account created & authenticated!');
          } catch (signupErr: any) {
            toast.error(signupErr.message || 'Authentication failed');
          }
        } else {
          toast.error(err.message || 'Authentication failed');
        }
      } finally {
        setAdminLoginLoading(false);
      }
    };

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#09090b] p-6 font-jakarta">
        <div className="w-full max-w-lg bg-white/5 border border-white/10 p-10 rounded-[40px] shadow-2xl animate-in fade-in zoom-in duration-500 relative overflow-hidden text-center backdrop-blur-xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-spice-accent/10 blur-[80px] rounded-full -mr-16 -mt-16"></div>
          
          <div className="relative z-10">
            <div className="inline-flex w-16 h-16 bg-spice-accent rounded-3xl items-center justify-center mb-6 shadow-xl shadow-spice-accent/20 rotate-6">
              <Package className="text-white w-8 h-8" />
            </div>
            <h1 className="text-2xl font-serif font-bold text-white mb-2 uppercase tracking-tight">
              Guild <span className="serif italic font-normal text-spice-accent">Archivist Portal</span>
            </h1>
            <p className="text-[10px] text-white/40 font-black uppercase tracking-[0.3em] mb-8">Admin Access & Content Management</p>
            
            {user ? (
              <div className="space-y-6 text-left bg-white/5 border border-white/10 p-6 rounded-3xl">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Authenticated Session</p>
                  <p className="text-sm font-bold text-white truncate">{user.email || user.phoneNumber || user.uid}</p>
                </div>

                {isTargetEmail ? (
                  <div className="space-y-3">
                    <p className="text-xs text-green-400 font-medium">
                      ✓ Authorized Admin email recognized ({user.email}). Click below to confirm High Archivist privileges.
                    </p>
                    <button
                      type="button"
                      onClick={handleElevateCurrentAccount}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-4 px-6 rounded-2xl uppercase tracking-wider text-[11px] font-black transition-all shadow-lg shadow-emerald-900/30 flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Activate Super Admin Privileges
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-amber-400 font-medium">
                      This account is not designated as an Admin. Expected: <span className="font-mono text-white">manjunathas788@gmail.com</span>
                    </p>
                    <button
                      type="button"
                      onClick={logout}
                      className="w-full bg-red-600/80 hover:bg-red-600 text-white py-3.5 px-6 rounded-2xl uppercase tracking-wider text-[10px] font-black transition-all"
                    >
                      Sign Out & Switch Account
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex bg-white/5 p-1 rounded-2xl border border-white/10 text-xs font-bold">
                  <button
                    type="button"
                    onClick={() => setLoginTab('google')}
                    className={`flex-1 py-2.5 rounded-xl transition-all ${loginTab === 'google' ? 'bg-spice-accent text-white shadow' : 'text-white/40 hover:text-white'}`}
                  >
                    Google Account
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginTab('email')}
                    className={`flex-1 py-2.5 rounded-xl transition-all ${loginTab === 'email' ? 'bg-spice-accent text-white shadow' : 'text-white/40 hover:text-white'}`}
                  >
                    Email / Password
                  </button>
                </div>

                {loginTab === 'google' ? (
                  <div className="space-y-4">
                    <p className="text-white/50 text-[11px] leading-relaxed">
                      Authenticate with your authorized administrator Google Account (<span className="text-spice-accent font-semibold">manjunathas788@gmail.com</span>).
                    </p>
                    <button 
                      onClick={handleSignIn}
                      className="w-full bg-spice-accent text-white py-4 px-8 rounded-2xl uppercase tracking-[0.2em] text-[10px] font-black hover:scale-105 transition-all duration-300 shadow-2xl shadow-spice-accent/20 flex items-center justify-center gap-3"
                    >
                      Authenticate Google Account
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleEmailLoginSubmit} className="space-y-4 text-left">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-white/50 mb-1 ml-1">Admin Email</label>
                      <input 
                        type="email" 
                        required 
                        value={adminEmail} 
                        onChange={(e) => setAdminEmail(e.target.value)} 
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white text-xs font-medium outline-none focus:border-spice-accent"
                        placeholder="manjunathas788@gmail.com"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-white/50 mb-1 ml-1">Password</label>
                      <input 
                        type="password" 
                        required 
                        value={adminPassword} 
                        onChange={(e) => setAdminPassword(e.target.value)} 
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white text-xs font-medium outline-none focus:border-spice-accent"
                        placeholder="••••••••"
                      />
                    </div>
                    <button 
                      type="submit" 
                      disabled={adminLoginLoading}
                      className="w-full bg-spice-accent text-white py-4 px-6 rounded-2xl uppercase tracking-wider text-[10px] font-black hover:scale-105 transition-all shadow-lg shadow-spice-accent/20 disabled:opacity-50"
                    >
                      {adminLoginLoading ? 'Verifying...' : 'Sign In as Administrator'}
                    </button>
                  </form>
                )}
              </div>
            )}
            
            <div className="pt-8 mt-8 border-t border-white/5 flex items-center justify-between">
              <Link href="/" className="text-[10px] uppercase font-black tracking-[0.2em] text-white/30 hover:text-white transition-colors">
                ← Public Store
              </Link>
              <Link href="/staff" className="text-[10px] uppercase font-black tracking-[0.2em] text-white/30 hover:text-white transition-colors">
                Staff Portal →
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sidebarItems = [
    { id: 'analytics', label: 'Home', icon: LayoutDashboard },
    { id: 'orders', label: 'Work Orders', icon: ClipboardList },
    { id: 'staff', label: 'Staff', icon: Users },
    { id: 'products', label: 'Assets', icon: Boxes },
    { id: 'facilities', label: 'Locations', icon: MapPin },
    { id: 'crm', label: 'Customers', icon: Contact },
    { id: 'categories', label: 'Categories', icon: Tag },
    { id: 'coupons', label: 'Coupons', icon: Ticket },
    { id: 'reviews', label: 'Feedback', icon: MessageSquare },
    { id: 'banners', label: 'Display', icon: Monitor },
  ];

  return (
    <div className="admin-dark-theme min-h-screen bg-black flex font-jakarta overflow-hidden text-white relative">
      {/* Mobile Drawer Backdrop */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-black flex flex-col h-screen overflow-hidden text-white shrink-0 transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 ${
        mobileSidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
      }`}>
        <div className="p-6 lg:p-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Package className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">Workr</span>
          </div>
          <button 
            onClick={() => setMobileSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-white/10 rounded-xl transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-white/80" />
          </button>
        </div>

        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto no-scrollbar">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id as any);
                  setMobileSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === item.id 
                    ? 'bg-white/10 text-white shadow-sm ring-1 ring-white/10' 
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className={`w-5 h-5 ${activeTab === item.id ? 'text-white' : 'text-white/60'}`} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-6 mt-auto border-t border-white/10">
          <div className="flex items-center gap-3 mb-6 p-2 hover:bg-white/5 rounded-2xl transition-colors cursor-pointer group">
            <img 
              src={user.photoURL || `https://ui-avatars.com/api/?name=${user.email}&background=random`} 
              className="w-10 h-10 rounded-xl border-2 border-white/20 group-hover:border-white transition-all shadow-lg" 
              alt="User"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">Archivist</p>
              <p className="text-[10px] text-white/50 truncate font-medium uppercase tracking-wider">{user.email?.split('@')[0]}</p>
            </div>
            <button onClick={logout} className="p-2 hover:bg-white/10 rounded-xl transition-colors" aria-label="Sign out">
              <LogOut className="w-4 h-4 text-white/60 hover:text-white" />
            </button>
          </div>
          <Link href="/" className="flex items-center justify-center gap-2 w-full py-2.5 px-3 rounded-xl bg-white/5 hover:bg-white/10 text-[11px] font-semibold text-white/70 hover:text-white transition-colors">
            <span>← Back to Store</span>
          </Link>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 h-screen overflow-y-auto bg-white m-0 lg:m-4 rounded-none lg:rounded-[40px] shadow-2xl shadow-blue-900/10 border border-slate-100 flex flex-col relative overflow-hidden min-w-0">
        {/* Background Accent */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50/50 blur-[120px] rounded-full -mr-48 -mt-48 pointer-events-none"></div>

        {/* Header */}
        <header className="px-4 py-3 sm:px-6 sm:py-5 lg:p-10 border-b border-slate-50 flex justify-between items-center bg-white/80 backdrop-blur-md sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden p-2.5 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 shrink-0 transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold tracking-tight text-[#0f172a] capitalize truncate">
                {sidebarItems.find(i => i.id === activeTab)?.label} <span className="text-slate-300 font-medium hidden sm:inline">Dashboard</span>
              </h1>
              <div className="flex items-center gap-2 mt-0.5 sm:mt-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0"></div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] truncate">Operational: Optimal</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2.5 sm:p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all relative"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
                {notifications.filter(n => !n.read).length > 0 && (
                  <span className="absolute top-2 right-2 w-4 h-4 bg-red-500 text-white text-[9px] font-black flex items-center justify-center rounded-full border-2 border-white">
                    {notifications.filter(n => !n.read).length}
                  </span>
                )}
              </button>
              
              <AnimatePresence>
                {showNotifications && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-4 w-[calc(100vw-2rem)] sm:w-96 max-w-[380px] bg-white border border-slate-100 shadow-2xl rounded-[32px] z-[100] overflow-hidden"
                  >
                    <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                      <h4 className="font-bold text-slate-900">Notifications</h4>
                      <button 
                        onClick={async () => {
                          const unread = notifications.filter(n => !n.read);
                          const batch = writeBatch(db);
                          unread.forEach(n => batch.update(doc(db, 'notifications', n.id), { read: true }));
                          await batch.commit();
                        }}
                        className="text-[10px] uppercase font-black tracking-widest text-[#2162eb] hover:underline"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto no-scrollbar">
                      {notifications.length === 0 ? (
                        <div className="p-12 text-center">
                          <div className="w-16 h-16 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-4 border border-slate-100">
                            <Bell className="w-6 h-6 text-slate-300" />
                          </div>
                          <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Awaiting Events</p>
                        </div>
                      ) : (
                        notifications.map(n => (
                          <div 
                            key={n.id} 
                            className={`p-6 border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors ${!n.read ? 'bg-blue-50/10' : ''}`}
                          >
                            <div className="flex items-start gap-4">
                              <div className={`mt-1 p-2 rounded-xl scale-90 ${n.read ? 'bg-slate-100 text-slate-400' : 'bg-blue-100 text-blue-600'}`}>
                                {n.read ? <MailOpen className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                              </div>
                              <div className="flex-1">
                                <p className={`text-sm font-bold ${n.read ? 'text-slate-500' : 'text-slate-900'}`}>{n.title}</p>
                                <p className="text-xs text-slate-400 mt-1 leading-relaxed line-clamp-2 font-medium">{n.message}</p>
                                <p className="text-[9px] text-slate-300 mt-2 font-bold uppercase tracking-widest">
                                  {n.createdAt?.toDate ? format(n.createdAt.toDate(), 'MMM dd • HH:mm') : 'Just now'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Content Body */}
        <div className="p-4 sm:p-6 lg:p-10 flex-1 relative z-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              {activeTab === 'analytics' && <AnalyticsDashboard orders={orders} onExportGst={exportToCSV} />}
              {activeTab === 'crm' && <CRMDashboard users={users} orders={orders} />}
              {activeTab === 'categories' && (
                <div className="space-y-10">
                  <div className="bg-white border rounded-[32px] p-8 border-slate-100 shadow-sm">
                    <h3 className="text-xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                      <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
                      {editingCategory ? `Update Archive: ${editingCategory.name}` : 'New Category Index'}
                    </h3>
                    <form onSubmit={handleSaveCategory} className="grid grid-cols-1 md:grid-cols-4 gap-8 items-end">
                      <div className="md:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Archive Identity</label>
                        <input required type="text" value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" placeholder="Name" />
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Priority Rank</label>
                        <input type="number" value={categoryForm.order} onChange={(e) => setCategoryForm({...categoryForm, order: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" />
                      </div>
                      <div className="md:col-span-1 flex items-center h-[54px] pt-4">
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <div className="relative">
                            <input type="checkbox" checked={categoryForm.isActive} onChange={(e) => setCategoryForm({...categoryForm, isActive: e.target.checked})} className="sr-only" />
                            <div className={`w-12 h-6 rounded-full transition-colors ${categoryForm.isActive ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
                            <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${categoryForm.isActive ? 'translate-x-6' : ''}`}></div>
                          </div>
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest group-hover:text-slate-900">Operational</span>
                        </label>
                      </div>
                      <div className="md:col-span-1 flex gap-4">
                        <button type="submit" className="flex-1 h-[54px] bg-[#2162eb] text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 active:scale-95 transition-all shadow-lg shadow-blue-600/20">
                          {editingCategory ? 'Commit Changes' : 'Create Entry'}
                        </button>
                        {editingCategory && (
                          <button type="button" onClick={() => { setEditingCategory(null); setCategoryForm({name: '', description: '', image: '', isActive: true, order: '0'}); }} className="w-[54px] h-[54px] rounded-2xl border border-slate-100 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all">
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Detailed Description</label>
                        <input type="text" value={categoryForm.description} onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" placeholder="..." />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Visual Reference (Image file or URL)</label>
                        <div className="flex gap-4 items-center">
                          <div className="relative w-14 h-14 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center overflow-hidden shrink-0 group/catimg shadow-sm hover:border-blue-600 transition-colors">
                            {categoryForm.image ? (
                              <>
                                <img src={categoryForm.image} className="w-full h-full object-cover" alt="Category preview" referrerPolicy="no-referrer" />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/catimg:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                                  <Upload className="w-4 h-4 text-white" />
                                </div>
                              </>
                            ) : (
                              <div className="text-slate-400 flex flex-col items-center justify-center cursor-pointer hover:text-blue-500 transition-colors">
                                <Plus className="w-4 h-4" />
                                <span className="text-[8px] font-bold uppercase tracking-wider">IMG</span>
                              </div>
                            )}
                            <input 
                              type="file" 
                              accept="image/*" 
                              className="absolute inset-0 opacity-0 cursor-pointer" 
                              onChange={(e) => handleImageUpload(e, (url) => setCategoryForm({...categoryForm, image: url}))} 
                            />
                          </div>
                          <div className="flex-1">
                            <input type="text" value={categoryForm.image} onChange={(e) => setCategoryForm({...categoryForm, image: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" placeholder="Or paste category image URL (https://)" />
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>

                  <div className="bg-white border rounded-[32px] border-slate-100 overflow-hidden shadow-sm">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                          <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Rank</th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Identity</th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Description</th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {categories.map(cat => (
                          <tr key={cat.id} className="group hover:bg-slate-50/30 transition-all">
                            <td className="px-8 py-6">
                              <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-xs text-slate-600 border border-slate-200">
                                {cat.order || 0}
                              </span>
                            </td>
                            <td className="px-8 py-6">
                              <div className="font-bold text-slate-900">{cat.name}</div>
                              <div className="text-[10px] text-slate-400 font-mono tracking-tight mt-1">{cat.id}</div>
                            </td>
                            <td className="px-8 py-6 text-xs text-slate-400 font-medium italic max-w-xs truncate">{cat.description || '-'}</td>
                            <td className="px-8 py-6">
                              {cat.isActive ? (
                                <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-600 text-[10px] font-black uppercase tracking-widest rounded-lg"><div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div> Operational</span>
                              ) : (
                                <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-widest rounded-lg">Offline</span>
                              )}
                            </td>
                            <td className="px-8 py-6 text-right">
                              <div className="flex justify-end gap-2">
                                <button 
                                  type="button"
                                  onClick={() => handleEditCategory(cat)} 
                                  className="px-3 py-2 text-blue-600 bg-blue-50 hover:bg-blue-600 hover:text-white rounded-xl transition-all shadow-sm flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                  <span>Edit</span>
                                </button>
                                <button 
                                  type="button"
                                  onClick={() => handleDeleteCategory(cat.id)} 
                                  className="p-2 text-red-500 bg-red-50 hover:bg-red-600 hover:text-white rounded-xl transition-all shadow-sm"
                                  title="Delete category"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {activeTab === 'orders' && (
                <div className="space-y-8 h-full flex flex-col">
                  <div className="flex flex-col lg:flex-row gap-6 items-center">
                      <div className="flex-1 w-full bg-slate-50 border border-slate-100 rounded-[24px] focus-within:bg-white focus-within:border-blue-600 transition-all shadow-sm flex items-center">
                        <Search className="ml-5 w-5 h-5 text-slate-300 group-focus-within:text-blue-600 transition-colors" />
                        <input 
                          type="text" 
                          placeholder="Scan Order Reference..."
                          value={orderSearch}
                          onChange={(e) => setOrderSearch(e.target.value)}
                          className="flex-1 px-4 py-5 bg-transparent border-none outline-none text-sm font-bold text-slate-900"
                        />
                      </div>
                    <div className="flex bg-slate-100 p-1.5 rounded-[24px] shadow-sm">
                      <button 
                        onClick={() => setOrderFilter('all')}
                        className={`px-8 py-3.5 text-[10px] font-black uppercase tracking-widest rounded-[20px] transition-all ${orderFilter === 'all' ? 'bg-white text-blue-600 shadow-lg shadow-blue-600/5' : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        Registry ({orders.length})
                      </button>
                      <button 
                        onClick={() => setOrderFilter('unassigned')}
                        className={`px-8 py-3.5 text-[10px] font-black uppercase tracking-widest rounded-[20px] transition-all ${orderFilter === 'unassigned' ? 'bg-white text-blue-600 shadow-lg shadow-blue-600/5' : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        Orphaned ({orders.filter(o => !o.facilityCenterId).length})
                      </button>
                    </div>
                    <button 
                      onClick={handleAutoAssignAll}
                      className="px-10 py-5 bg-white border border-slate-100 text-blue-600 rounded-[24px] font-black uppercase tracking-widest text-[10px] hover:bg-blue-600 hover:text-white transition-all shadow-sm flex items-center gap-3"
                    >
                      <Workflow className="w-5 h-5" />
                      Auto-Route
                    </button>
                  </div>

                  <div className="bg-white border rounded-[40px] border-slate-100 overflow-hidden shadow-2xl shadow-blue-900/5 flex-1 flex flex-col">
                    <div className="overflow-x-auto h-full no-scrollbar">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Archival Ref</th>
                            <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Initiator</th>
                            <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Allocation</th>
                            <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                            <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {orders
                            .filter(order => {
                              const matchesFilter = orderFilter === 'all' || !order.facilityCenterId;
                              const searchTerm = orderSearch.toLowerCase();
                              return matchesFilter && (
                                (order.customerName || '').toLowerCase().includes(searchTerm) ||
                                (order.orderNumber || '').includes(searchTerm) ||
                                order.id.toLowerCase().includes(searchTerm)
                              );
                            })
                            .map(order => (
                              <tr key={order.id} className="group hover:bg-blue-50/30 transition-all">
                                <td className="px-10 py-8">
                                  <div className="font-bold text-slate-900 text-sm tracking-tight mb-1">
                                    #{order.orderNumber || order.id.slice(0, 8)}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-3 h-3 text-slate-300" />
                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                      {order.createdAt?.toDate ? format(order.createdAt.toDate(), 'MMM dd, HH:mm') : 'N/A'}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-10 py-8">
                                  <div className="font-bold text-slate-900 text-sm">{order.customerName}</div>
                                  <div className="text-[11px] text-slate-400 mt-1 font-medium">{order.customerPhone}</div>
                                </td>
                                <td className="px-10 py-8">
                                  <select 
                                    value={order.facilityCenterId || ''}
                                    onChange={(e) => handleManualAssign(order.id, e.target.value)}
                                    className="text-[10px] font-black uppercase tracking-[0.2em] px-4 py-2 bg-slate-100 border-none rounded-xl text-slate-600 focus:ring-2 ring-blue-600/10 cursor-pointer hover:bg-slate-200 transition-colors"
                                  >
                                    <option value="">Unassigned</option>
                                    {facilityCenters.map(f => (
                                      <option key={f.id} value={f.id}>{f.name}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-10 py-8">
                                  <select
                                    value={order.status || 'Received'}
                                    onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                                    className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider outline-none cursor-pointer transition-all border ${
                                      order.status === 'Delivered' || order.status === 'Completed'
                                        ? 'bg-emerald-600 text-white !text-white border-emerald-600 font-bold shadow-sm' 
                                        : order.status === 'Processing' || order.status === 'Picking & Packing'
                                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                                        : 'bg-orange-50 text-orange-700 border-orange-200'
                                    }`}
                                    style={order.status === 'Delivered' || order.status === 'Completed' ? { color: '#ffffff' } : {}}
                                  >
                                    <option value="Received">Received</option>
                                    <option value="Picking & Packing">Picking & Packing</option>
                                    <option value="Ready for Dispatch">Ready for Dispatch</option>
                                    <option value="Processing">Processing</option>
                                    <option value="Delivered">Delivered</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Returned">Returned</option>
                                  </select>
                                </td>
                                <td className="px-10 py-8 text-right">
                                  <div className="flex justify-end gap-2">
                                    <button 
                                      type="button" 
                                      onClick={() => handlePrintLabel(order)} 
                                      className="p-2.5 bg-slate-50 text-slate-600 hover:text-white hover:bg-slate-900 rounded-xl transition-all shadow-sm"
                                      title="Print packing label"
                                    >
                                      <Printer className="w-4 h-4" />
                                    </button>
                                    <button 
                                      type="button" 
                                      onClick={() => handleDeleteOrder(order.id)} 
                                      className="p-2.5 bg-red-50 text-red-500 hover:text-white hover:bg-red-500 rounded-xl transition-all shadow-sm"
                                      title="Delete order"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'facilities' && (
                <div className="space-y-10">
                  <div className="bg-white border rounded-[32px] p-8 border-slate-100 shadow-sm">
                    <h3 className="text-xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                      <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
                      Center Registry
                    </h3>
                    <form onSubmit={handleCreateFacility} className="grid grid-cols-1 md:grid-cols-4 gap-8">
                      <div className="md:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Center Identity</label>
                        <input required type="text" value={newFacility.name} onChange={(e) => setNewFacility({...newFacility, name: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" placeholder="Name" />
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Archive Code</label>
                        <input required type="text" value={newFacility.code} onChange={(e) => setNewFacility({...newFacility, code: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" placeholder="e.g. BLR01" />
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Zone (Pincode)</label>
                        <input required type="text" value={newFacility.pincode} onChange={(e) => setNewFacility({...newFacility, pincode: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" placeholder="e.g. 560001" />
                      </div>
                      <div className="md:col-span-1 flex items-end">
                        <button type="submit" className="w-full h-[54px] bg-[#2162eb] text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 active:scale-95 transition-all shadow-lg shadow-blue-600/20">
                          Deploy Center
                        </button>
                      </div>
                    </form>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {facilityCenters.map(center => (
                      <div key={center.id} className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm group hover:shadow-xl hover:shadow-blue-900/5 transition-all relative overflow-hidden">
                        <div className={`absolute top-0 left-0 w-1.5 h-full transition-colors ${center.isActive ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
                        <div className="flex justify-between items-start mb-6">
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1">{center.code}</p>
                            <h4 className="text-lg font-bold text-slate-900">{center.name}</h4>
                          </div>
                          <button onClick={() => handleDeleteFacility(center.id)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="flex items-center gap-4 py-4 border-y border-slate-50 mb-6">
                          <MapPin className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-bold text-black">Servicing Zone: {center.pincode || (center.pincodes && center.pincodes[0])}</span>
                        </div>
                        <button 
                          onClick={() => handleToggleFacility(center.id, center.isActive)}
                          className={`w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                            center.isActive 
                              ? 'bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-600' 
                              : 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                          }`}
                        >
                          {center.isActive ? 'Suspend Operations' : 'Activate Hub'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'staff' && (
                <div className="space-y-10">
                  <div className="bg-white border rounded-[32px] p-8 border-slate-100 shadow-sm">
                    <h3 className="text-xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                      <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
                      Staff Onboarding
                    </h3>
                    <form onSubmit={handleStaffSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-6">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Personnel Name</label>
                        <input required type="text" value={staffForm.name} onChange={(e) => setStaffForm({...staffForm, name: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Email Address</label>
                        <input required type="email" value={staffForm.email} onChange={(e) => setStaffForm({...staffForm, email: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Contact Channel</label>
                        <input required type="text" value={staffForm.phone} onChange={(e) => setStaffForm({...staffForm, phone: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Station Allocation</label>
                        <select value={staffForm.facilityCenterId} onChange={(e) => setStaffForm({...staffForm, facilityCenterId: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-slate-900 appearance-none">
                          <option value="">Awaiting Assignment</option>
                          {facilityCenters.map(f => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-end">
                        <button type="submit" className="w-full h-[54px] bg-[#2162eb] text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 active:scale-95 transition-all shadow-lg shadow-blue-600/20">
                          {editingStaffId ? 'Update Record' : 'Enlist Personnel'}
                        </button>
                      </div>
                    </form>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {staffList.map(person => (
                      <div key={person.id} className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm flex flex-col group">
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100">
                            <Users className="w-6 h-6 text-slate-300" />
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-slate-900">{person.name}</h4>
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{person.email || 'No Email'}</p>
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">{person.phone}</p>
                          </div>
                        </div>
                        <div className="py-4 border-t border-slate-50 mb-6">
                           <p className="text-[10px] font-black uppercase tracking-widest text-slate-300 mb-2">Station</p>
                           <p className="text-sm font-bold text-blue-600">{facilityCenters.find(f => f.id === person.facilityCenterId)?.name || 'Reserve'}</p>
                        </div>
                        <div className="mt-auto flex gap-3 pt-6 border-t border-slate-50">
                          <button onClick={() => { setEditingStaffId(person.id); setStaffForm({name: person.name, email: person.email || '', phone: person.phone, facilityCenterId: person.facilityCenterId}); }} className="flex-1 py-4 px-4 bg-slate-50 text-slate-400 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-50 hover:text-blue-600 transition-all">Edit</button>
                          <button onClick={() => handleDeleteStaff(person.id)} className="w-14 h-14 flex items-center justify-center bg-slate-50 text-slate-400 rounded-2xl hover:bg-red-50 hover:text-red-500 transition-all"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'products' && (
                <div className="space-y-10">
                  <div 
                    id="product-form-section" 
                    className={`bg-white border rounded-[32px] p-8 shadow-sm transition-all duration-300 ${
                      editingProduct ? 'border-blue-500 ring-4 ring-blue-500/10 shadow-lg' : 'border-slate-100'
                    }`}
                  >
                    {editingProduct && (
                      <div className="flex flex-wrap items-center justify-between p-4 mb-6 bg-blue-50/80 border border-blue-200 rounded-2xl gap-3">
                        <div className="flex items-center gap-3">
                          <span className="w-3 h-3 rounded-full bg-blue-600 animate-ping"></span>
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-blue-800">Modifying Product Asset</p>
                            <p className="text-sm font-bold text-slate-900">{editingProduct.name} <span className="text-xs font-normal text-slate-400">({editingProduct.id})</span></p>
                          </div>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => {
                            setEditingProduct(null);
                            setProductForm({ 
                              name: '', 
                              description: '', 
                              price: '', 
                              image: '', 
                              customImage: '',
                              category: 'powder', 
                              variants: [{ weight: '1 Unit', price: 0, costPrice: 0, image: '', stock: 0 }], 
                              stock: '', 
                              unit: 'Units',
                              pricePerGram: '',
                              gstPercentage: '5',
                              hsnCode: '',
                              order: '0',
                              speciality: '',
                              season: '',
                              source: '',
                              isOutOfStock: false,
                              unitStocks: {}
                            });
                            toast('Cancelled editing');
                          }}
                          className="px-4 py-2 bg-white text-slate-700 hover:bg-red-50 hover:text-red-600 rounded-xl text-xs font-bold transition-all border border-slate-200 shadow-sm"
                        >
                          ✕ Cancel Edit
                        </button>
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                      <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
                      {editingProduct ? `Refine Asset: ${editingProduct.name}` : 'Archive New Asset'}
                    </h3>
                    <form onSubmit={handleSaveProduct} className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <div className="space-y-6">
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Asset Identity</label>
                          <input required value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" placeholder="Name" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Classification</label>
                          <select value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black appearance-none">
                            {categories.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Base Valuation (₹)</label>
                          <input required type="number" value={productForm.price} onChange={(e) => setProductForm({...productForm, price: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" />
                        </div>
                        
                        {/* Out of Stock Toggle */}
                        <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl mt-4">
                          <div>
                            <span className="block text-xs font-black uppercase text-slate-800">Mark Out of Stock</span>
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wide">Blur on customer screens</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setProductForm({ ...productForm, isOutOfStock: !productForm.isOutOfStock })}
                            className={`w-12 h-6 flex items-center rounded-full p-1 transition-all duration-300 ${productForm.isOutOfStock ? 'bg-red-500' : 'bg-slate-300'}`}
                          >
                            <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${productForm.isOutOfStock ? 'translate-x-6' : 'translate-x-0'}`} />
                          </button>
                        </div>

                        {/* Facility Stock per Variant */}
                        {facilityCenters.length > 0 && (
                          <div className="space-y-4 pt-2">
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Facility Stocks (Units)</label>
                            <div className="space-y-3 max-h-[200px] overflow-y-auto no-scrollbar pr-1">
                              {productForm.variants.map((v, vIdx) => (
                                <div key={vIdx} className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                                  <p className="text-[9px] font-black uppercase tracking-wider text-blue-600 mb-2">Variant: {v.weight || `Variant ${vIdx + 1}`}</p>
                                  <div className="space-y-2">
                                    {facilityCenters.map(center => {
                                      const key = `${vIdx}_${center.id}`;
                                      return (
                                        <div key={center.id} className="flex items-center justify-between gap-2">
                                          <span className="text-[10px] font-semibold text-slate-600 truncate flex-1">{center.name}</span>
                                          <input 
                                            type="number"
                                            value={productForm.unitStocks[key] || ''}
                                            onChange={(e) => setProductForm({
                                              ...productForm,
                                              unitStocks: {
                                                ...productForm.unitStocks,
                                                [key]: e.target.value
                                              }
                                            })}
                                            className="w-16 px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-bold text-slate-800 text-right outline-none focus:border-blue-600"
                                            placeholder="0"
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Variant Configuration</label>
                          <div className="space-y-4 max-h-[420px] overflow-y-auto no-scrollbar pr-2">
                            {productForm.variants.map((variant, idx) => (
                              <div key={idx} className="p-4 bg-slate-50 rounded-3xl border border-slate-100 flex flex-col gap-3 group shadow-sm hover:border-slate-200 transition-all">
                                <div className="flex gap-3 items-center">
                                  {/* Thumbnail & File Upload Trigger */}
                                  <div 
                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    onDrop={(e) => { 
                                      e.preventDefault(); 
                                      e.stopPropagation(); 
                                      const file = e.dataTransfer.files?.[0]; 
                                      if (file) { processImageFile(file, (url) => handleUpdateVariant(idx, 'image', url)); } 
                                    }}
                                    className="relative w-12 h-12 bg-white rounded-xl border border-slate-200/80 flex items-center justify-center overflow-hidden shrink-0 group/img shadow-sm hover:border-blue-600 transition-all cursor-pointer"
                                    title="Drag & drop image here or click to upload"
                                  >
                                    {variant.image ? (
                                      <>
                                        <img src={variant.image} className="w-full h-full object-cover" alt="Variant preview" referrerPolicy="no-referrer" />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                                          <Upload className="w-4 h-4 text-white" />
                                        </div>
                                      </>
                                    ) : (
                                      <div className="text-slate-400 flex flex-col items-center justify-center hover:text-blue-500 transition-colors">
                                        <Upload className="w-3.5 h-3.5" />
                                        <span className="text-[7px] font-bold uppercase tracking-wider mt-0.5">LOCAL</span>
                                      </div>
                                    )}
                                    <input 
                                      type="file" 
                                      accept="image/*" 
                                      className="absolute inset-0 opacity-0 cursor-pointer" 
                                      onChange={(e) => handleImageUpload(e, (url) => handleUpdateVariant(idx, 'image', url))} 
                                    />
                                  </div>

                                  {/* Inputs */}
                                  <div className="flex-1 flex gap-2">
                                    <input 
                                      required 
                                      placeholder="Size (e.g. 100g)" 
                                      value={variant.weight} 
                                      onChange={(e) => handleUpdateVariant(idx, 'weight', e.target.value)} 
                                      className="w-1/2 px-3 py-1.5 bg-white border border-slate-150 rounded-xl text-[11px] font-bold text-slate-900 outline-none focus:border-blue-600" 
                                    />
                                    <input 
                                      required 
                                      type="number" 
                                      placeholder="Price (₹)" 
                                      value={variant.price || ''} 
                                      onChange={(e) => handleUpdateVariant(idx, 'price', e.target.value)} 
                                      className="w-1/2 px-3 py-1.5 bg-white border border-slate-150 rounded-xl text-[11px] font-bold text-blue-600 outline-none focus:border-blue-600" 
                                    />
                                  </div>

                                  {/* Delete Button */}
                                  {productForm.variants.length > 1 && (
                                    <button 
                                      type="button" 
                                      onClick={() => handleRemoveVariant(idx)} 
                                      className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>

                                {/* URL Textbox row */}
                                <div className="flex gap-2 items-center">
                                  <input 
                                    type="text" 
                                    placeholder="Or paste variant image URL..." 
                                    value={variant.image || ''} 
                                    onChange={(e) => handleUpdateVariant(idx, 'image', e.target.value)} 
                                    className="flex-1 px-3 py-1 bg-white border border-slate-150 rounded-lg text-[9px] font-semibold text-slate-500 outline-none focus:border-blue-600" 
                                  />
                                  {variant.image && (
                                    <button
                                      type="button"
                                      onClick={() => handleUpdateVariant(idx, 'image', '')}
                                      className="text-[9px] text-red-500 hover:underline font-bold uppercase tracking-wider whitespace-nowrap"
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                            <button type="button" onClick={handleAddVariant} className="w-full py-3 border border-dashed border-slate-200 rounded-2xl text-[9px] font-black uppercase tracking-widest text-slate-400 hover:border-blue-600 hover:text-blue-600 transition-all">+ Add New Variant</button>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Asset Image reference</label>
                          
                          {/* Dedicated Upload Option Card from Local Storage */}
                          {!productForm.image ? (
                            <div 
                              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              onDrop={(e) => { 
                                e.preventDefault(); 
                                e.stopPropagation(); 
                                const file = e.dataTransfer.files?.[0]; 
                                if (file) { processImageFile(file, (url) => setProductForm({...productForm, image: url})); } 
                              }}
                              className="relative flex flex-col items-center justify-center border-2 border-dashed border-slate-200 hover:border-blue-500 rounded-3xl p-8 text-center cursor-pointer transition-all bg-slate-50 hover:bg-slate-100/50 group/dropzone min-h-[160px]"
                            >
                              <input 
                                type="file" 
                                accept="image/*" 
                                className="absolute inset-0 opacity-0 cursor-pointer z-10" 
                                onChange={(e) => handleImageUpload(e, (url) => setProductForm({...productForm, image: url}))} 
                              />
                              <div className="p-4 bg-white rounded-2xl shadow-sm border border-slate-100 group-hover/dropzone:scale-110 mb-3 transition-transform text-slate-400 group-hover/dropzone:text-blue-600">
                                <Upload className="w-6 h-6" />
                              </div>
                              <p className="text-xs font-black text-slate-800 uppercase tracking-wider">Upload local reference image</p>
                              <p className="text-[10px] text-slate-400 font-bold mt-1.5 uppercase tracking-widest">Drag & Drop here or tap to select</p>
                            </div>
                          ) : (
                            <div className="relative rounded-3xl overflow-hidden border border-slate-150 shadow-sm bg-slate-50 p-4 flex flex-col items-center justify-center">
                              <div className="relative w-full h-44 rounded-2xl overflow-hidden bg-white flex items-center justify-center border border-slate-100/80 group/preview hover:shadow-inner transition-shadow">
                                <img src={productForm.image} className="max-h-full max-w-full object-contain p-2" alt="Product reference" referrerPolicy="no-referrer" />
                                <div className="absolute inset-0 bg-black/45 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center gap-2 cursor-pointer">
                                  <div className="relative py-2.5 px-4 bg-white text-slate-900 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-md hover:scale-105 active:scale-95 transition-all flex items-center gap-1.5">
                                    <Upload className="w-3.5 h-3.5 text-blue-600" />
                                    <span>Browse New</span>
                                    <input 
                                      type="file" 
                                      accept="image/*" 
                                      className="absolute inset-0 opacity-0 cursor-pointer" 
                                      onChange={(e) => handleImageUpload(e, (url) => setProductForm({...productForm, image: url}))} 
                                    />
                                  </div>
                                  <button 
                                    type="button" 
                                    onClick={() => setProductForm({...productForm, image: ''})} 
                                    className="py-2.5 px-4 bg-red-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-md hover:bg-red-700 hover:scale-105 active:scale-95 transition-all flex items-center gap-1.5 z-20"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                    <span>Remove</span>
                                  </button>
                                </div>
                              </div>
                              <div className="w-full mt-3 flex items-center gap-2 px-1">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Loaded Source:</span>
                                <span className="text-[10px] font-bold text-slate-600 truncate flex-1">{productForm.image.startsWith('data:') ? 'Local Image File (Optimized Base64)' : productForm.image}</span>
                              </div>
                            </div>
                          )}

                          {/* Alternative text input link row */}
                          <div className="mt-4">
                            <label className="block text-[8px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Or provide alternative external image URL Link</label>
                            <input 
                              required={!productForm.image}
                              value={productForm.image} 
                              onChange={(e) => setProductForm({...productForm, image: e.target.value})} 
                              className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-xs font-bold text-black" 
                              placeholder="https://images.unsplash.com/example-link..." 
                            />
                          </div>
                        </div>
                         <div className="flex gap-4">
                          <button type="submit" className="flex-1 h-[54px] bg-[#2162eb] text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 active:scale-95 transition-all shadow-lg shadow-blue-600/20">
                            {editingProduct ? 'Update Asset' : 'Archive Asset'}
                          </button>
                          {editingProduct && (
                            <button type="button" onClick={() => setEditingProduct(null)} className="w-[54px] h-[54px] rounded-2xl border border-slate-100 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all">
                              <X className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {dbProducts
                      .filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()))
                      .map(product => (
                      <div key={product.id} className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm group hover:shadow-xl hover:shadow-blue-900/5 transition-all relative overflow-hidden">
                        <div className="aspect-square rounded-[32px] bg-slate-50 border border-slate-100 mb-6 p-6 overflow-hidden relative">
                           <img src={product.image} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500" alt={product.name} referrerPolicy="no-referrer" />
                           <div className="absolute top-4 right-4 px-3 py-1.5 bg-white shadow-sm rounded-xl text-[10px] font-black uppercase tracking-widest text-[#2162eb]">
                              ₹{product.price}
                           </div>
                        </div>
                        <div className="mb-6">
                           <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{categories.find(c => c.id === product.category)?.name || 'Archive'}</p>
                           <h4 className="text-xl font-bold text-slate-900 line-clamp-1">{product.name}</h4>
                        </div>
                        <div className="flex gap-2 mb-8 flex-wrap">
                           {product.variants?.map((v: any, i: number) => (
                             <span key={i} className="px-3 py-1 bg-slate-50 text-[10px] font-bold text-slate-400 rounded-lg border border-slate-100">{v.weight}</span>
                           ))}
                        </div>
                        <div className="flex gap-2 pt-3 border-t border-slate-100">
                           <button 
                             type="button"
                             onClick={() => handleEditProduct(product)} 
                             className={`flex-1 py-3 px-3 rounded-2xl font-black uppercase tracking-wider text-[10px] transition-all flex items-center justify-center gap-1.5 shadow-sm ${
                               editingProduct?.id === product.id 
                                 ? 'bg-blue-600 text-white shadow-blue-600/30' 
                                 : 'bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white'
                             }`}
                           >
                             <Edit2 className="w-3.5 h-3.5" />
                             <span>{editingProduct?.id === product.id ? 'In Editor' : 'Modify Asset'}</span>
                           </button>
                           <button 
                             type="button"
                             onClick={() => handleDeleteProduct(product.id)} 
                             className="w-12 h-11 bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 rounded-2xl flex items-center justify-center transition-all shadow-sm"
                             title="Delete product asset"
                           >
                             <Trash2 className="w-4 h-4" />
                           </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'coupons' && (
                <div className="space-y-10">
                  <div className="bg-white border rounded-[32px] p-8 border-slate-100 shadow-sm">
                    <h3 className="text-xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                      <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
                      Token Generation
                    </h3>
                    <form onSubmit={handleCreateCoupon} className="grid grid-cols-1 md:grid-cols-5 gap-8 items-end">
                      <div className="md:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Voucher Code</label>
                        <input required type="text" value={newCoupon.code} onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" placeholder="E.G. WIN2024" />
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Reduction Type</label>
                        <select value={newCoupon.type} onChange={(e) => setNewCoupon({...newCoupon, type: e.target.value as 'percent' | 'flat'})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black appearance-none">
                          <option value="percent">Percentage (%)</option>
                          <option value="flat">Flat Value (₹)</option>
                        </select>
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Magnitude</label>
                        <input required type="number" value={newCoupon.value} onChange={(e) => setNewCoupon({...newCoupon, value: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black" />
                      </div>
                      <div className="md:col-span-1 pb-4">
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <input type="checkbox" checked={newCoupon.isSingleUse} onChange={(e) => setNewCoupon({...newCoupon, isSingleUse: e.target.checked})} className="sr-only" />
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${newCoupon.isSingleUse ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-100 text-slate-400 border border-slate-100'}`}>
                            <Ticket className="w-4 h-4" />
                          </div>
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest group-hover:text-slate-900">Single Use</span>
                        </label>
                      </div>
                      <div className="md:col-span-1">
                        <button type="submit" className="w-full h-[54px] bg-[#2162eb] text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 active:scale-95 transition-all shadow-lg shadow-blue-600/20">
                          Create
                        </button>
                      </div>
                    </form>
                  </div>

                  <div className="bg-white border rounded-[40px] border-slate-100 overflow-hidden shadow-sm">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                          <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Token ID</th>
                          <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Yield</th>
                          <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Constraint</th>
                          <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Operational</th>
                          <th className="px-10 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {coupons.map(coupon => (
                          <tr key={coupon.id} className="group hover:bg-blue-50/30 transition-all">
                            <td className="px-10 py-8">
                               <div className="font-black text-slate-900 tracking-widest text-lg">{coupon.code}</div>
                            </td>
                            <td className="px-10 py-8">
                               <div className="font-bold text-blue-600">{coupon.type === 'percent' ? `${coupon.value}%` : `₹${coupon.value}`}</div>
                               <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">Reduction</p>
                            </td>
                            <td className="px-10 py-8">
                               <span className={`inline-flex items-center gap-2 px-3 py-1 scale-90 rounded-lg text-[9px] font-black uppercase tracking-widest ${coupon.isSingleUse ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
                                 {coupon.isSingleUse ? 'Exclusive' : 'Multiple'}
                               </span>
                            </td>
                            <td className="px-10 py-8">
                               <button 
                                 onClick={() => handleToggleCoupon(coupon.id, coupon.isActive)}
                                 className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                                   coupon.isActive ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-400'
                                 }`}
                               >
                                 {coupon.isActive ? 'Active' : 'Offline'}
                               </button>
                            </td>
                            <td className="px-10 py-8 text-right">
                               <button onClick={() => handleDeleteCoupon(coupon.id)} className="p-3 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"><Trash2 className="w-4 h-4" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="space-y-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {reviews.map(review => (
                      <div key={review.id} className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm relative group">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center font-bold text-slate-400 border border-slate-100">
                               {review.customerName?.charAt(0)}
                            </div>
                            <div>
                               <h4 className="text-sm font-bold text-slate-900">{review.customerName}</h4>
                               <div className="flex gap-0.5 mt-1">
                                 {[...Array(5)].map((_, i) => (
                                   <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-blue-500 fill-blue-500' : 'text-slate-200'}`} />
                                 ))}
                               </div>
                            </div>
                          </div>
                          <div className="text-right">
                             <p className="text-[9px] text-slate-300 font-bold uppercase tracking-widest">{review.createdAt?.toDate ? format(review.createdAt.toDate(), 'MMM dd, yyyy') : 'Recently'}</p>
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed font-medium mb-8">"{review.comment}"</p>
                        <div className="flex gap-3 justify-end items-center translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                           <button onClick={() => handleEditReviewAdmin(review)} className="px-6 py-3 bg-slate-50 text-slate-400 rounded-2xl font-black uppercase tracking-widest text-[9px] hover:bg-blue-50 hover:text-blue-600 transition-all">Edit Feedback</button>
                           <button onClick={() => handleDeleteReviewAdmin(review.id)} className="w-11 h-11 bg-slate-50 text-slate-300 rounded-2xl flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-all"><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <AnimatePresence>
                    {editingReview && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-sm">
                        <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} className="bg-white w-full max-w-lg rounded-[48px] p-12 shadow-2xl overflow-hidden relative">
                           <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/5 blur-[80px] rounded-full -mr-16 -mt-16"></div>
                           <h3 className="text-2xl font-bold text-slate-900 mb-8 border-l-4 border-blue-600 pl-6">Refine Feedback</h3>
                           <form onSubmit={handleSaveReviewAdmin} className="space-y-8">
                             <div>
                               <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 ml-1">Quality Score</label>
                               <div className="flex gap-4">
                                 {[1,2,3,4,5].map(star => (
                                   <button key={star} type="button" onClick={() => setReviewEditForm({...reviewEditForm, rating: star})} className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${reviewEditForm.rating >= star ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-50 text-slate-300'}`}>
                                     <Star className={`w-5 h-5 ${reviewEditForm.rating >= star ? 'fill-white' : ''}`} />
                                   </button>
                                 ))}
                               </div>
                             </div>
                             <div>
                               <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 ml-1">Commentary</label>
                               <textarea value={reviewEditForm.comment} onChange={(e) => setReviewEditForm({...reviewEditForm, comment: e.target.value})} className="w-full px-6 py-6 bg-slate-50 border border-slate-100 rounded-[32px] focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black resize-none" rows={4} />
                             </div>
                             <div className="flex gap-4">
                               <button type="submit" className="flex-1 py-5 bg-[#2162eb] text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 transition-all shadow-lg shadow-blue-600/20">Commit Changes</button>
                               <button type="button" onClick={() => setEditingReview(null)} className="flex-1 py-5 bg-slate-100 text-slate-400 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-200 transition-all">Abort</button>
                             </div>
                           </form>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {activeTab === 'banners' && (
                <div className="space-y-10">
                   <div className="bg-white border rounded-[48px] p-12 border-slate-100 shadow-sm relative overflow-hidden">
                     <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 blur-[100px] rounded-full -mr-32 -mt-32"></div>
                     <h3 className="text-2xl font-bold text-slate-900 mb-10 flex items-center gap-4">
                        <Monitor className="w-7 h-7 text-blue-600" />
                        Hero Banner Settings
                     </h3>
                     <form onSubmit={handleSaveBanner} className="space-y-10">
                        <div className="relative group">
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 ml-1">Display Asset URL</label>
                          <input required type="text" value={bannerSettings.bannerImage} onChange={(e) => setBannerSettings({...bannerSettings, bannerImage: e.target.value})} className="w-full px-8 py-6 bg-slate-50 border border-slate-100 rounded-[32px] focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-bold text-black pr-32" placeholder="https://" />
                          <div className="absolute right-4 top-[50px] flex gap-2">
                             <label className="p-4 bg-white text-slate-400 border border-slate-100 rounded-2xl cursor-pointer hover:bg-slate-900 hover:text-white transition-all shadow-sm">
                                <Upload className="w-4 h-4" />
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, (url) => setBannerSettings({...bannerSettings, bannerImage: url}))} />
                             </label>
                          </div>
                        </div>

                        {bannerSettings.bannerImage && (
                          <div className="rounded-[40px] overflow-hidden border-8 border-slate-50 shadow-inner group relative">
                             <img src={bannerSettings.bannerImage} className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-700" alt="Banner Preview" referrerPolicy="no-referrer" />
                             <div className="absolute inset-0 bg-blue-600/10 mix-blend-overlay"></div>
                             <div className="absolute bottom-8 left-8 bg-white/80 backdrop-blur-xl px-6 py-3 rounded-2xl border border-white/40">
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Live Preview Identity</p>
                             </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-6">
                           <p className="text-xs text-slate-400 font-medium max-w-sm">Ensure images are at least 1920x1080 for optimal high-resolution display on client terminals.</p>
                           <button type="submit" disabled={savingBanner} className={`px-12 py-5 bg-[#2162eb] text-white rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all shadow-lg shadow-blue-600/20 ${savingBanner ? 'opacity-50 scale-95' : 'hover:scale-105'}`}>
                             {savingBanner ? 'Syncing...' : 'Deploy Asset'}
                           </button>
                        </div>
                     </form>
                   </div>
                </div>
              )}

              {activeTab === 'autorouting' && (
                <div className="space-y-10">
                   <div className="bg-white border rounded-[48px] p-12 border-slate-100 shadow-sm relative overflow-hidden">
                     <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 blur-[100px] rounded-full -mr-32 -mt-32"></div>
                     <div className="max-w-2xl">
                        <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-4">
                           <Workflow className="w-7 h-7 text-blue-600" />
                           Autonomous Logistics Routing
                        </h3>
                        <p className="text-slate-400 font-medium text-sm leading-relaxed mb-12">Batch assign orphaned work orders to designated operational facilities based on zone availability and current load capacity.</p>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-end">
                        <div className="md:col-span-2">
                           <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 ml-1">Target Distribution Center</label>
                           <select 
                             value={routingUnitId}
                             onChange={(e) => setRoutingUnitId(e.target.value)}
                             className="w-full px-8 py-6 bg-slate-50 border border-slate-100 rounded-[32px] focus:bg-white focus:border-blue-600 outline-none transition-all text-sm font-black text-slate-900 uppercase tracking-widest appearance-none"
                           >
                             <option value="">Awaiting Hub Selection</option>
                             {facilityCenters.filter(f => f.isActive).map(f => (
                               <option key={f.id} value={f.id}>{f.name} ({f.code})</option>
                             ))}
                           </select>
                        </div>
                        <button 
                          disabled={!routingUnitId}
                          onClick={async () => {
                            const unitId = routingUnitId;
                            if (!unitId) return;
                            const unassigned = orders.filter(o => !o.facilityCenterId);
                            if (unassigned.length === 0) { toast.success('Queue is empty'); return; }
                            if (!window.confirm(`Initiate routing for ${unassigned.length} orders?`)) return;
                            const unit = facilityCenters.find(f => f.id === unitId);
                            toast.loading('Syncing distribution...', { id: 'bulk-route' });
                            try {
                              const chunkSize = 200; // Each order has 2 operations (update order + create notification)
                              
                              for (let i = 0; i < unassigned.length; i += chunkSize) {
                                const batch = writeBatch(db);
                                const currentChunk = unassigned.slice(i, i + chunkSize);
                                
                                for (const order of currentChunk) {
                                  batch.update(doc(db, 'orders', order.id), { facilityCenterId: unitId });
                                  
                                  const notifRef = doc(collection(db, 'notifications'));
                                  batch.set(notifRef, {
                                    title: 'Logistics Route',
                                    message: `Order #${order.orderNumber || order.id.slice(0,8)} routed to: ${unit?.name}`,
                                    type: 'facility',
                                    read: false,
                                    createdAt: serverTimestamp(),
                                    referenceId: order.id
                                  });
                                }
                                await batch.commit();
                              }
                              
                              toast.success('Batch routing sequence complete', { id: 'bulk-route' });
                              setRoutingUnitId('');
                            } catch (e) {
                              toast.error('Routing sequence interrupted', { id: 'bulk-route' });
                            }
                          }}
                          className="w-full h-[68px] bg-slate-900 text-white rounded-[32px] font-black uppercase tracking-widest text-[10px] hover:scale-105 active:scale-95 transition-all shadow-xl disabled:opacity-30 disabled:scale-100"
                        >
                          Execute Assign Sequence
                        </button>
                     </div>

                     <div className="mt-16 pt-16 border-t border-slate-50">
                        <div className="flex items-center justify-between mb-10">
                           <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Orphaned Queue Status</h4>
                           <span className="px-4 py-1.5 bg-red-50 text-red-600 rounded-full text-[9px] font-black uppercase tracking-widest border border-red-100">
                             {orders.filter(o => !o.facilityCenterId).length} Units Awaiting Allocation
                           </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                           {orders.filter(o => !o.facilityCenterId).map(order => (
                             <div key={order.id} className="p-6 bg-slate-50/50 border border-slate-50 rounded-2xl flex justify-between items-center group hover:bg-white hover:border-slate-100 hover:shadow-lg transition-all">
                                <div>
                                   <p className="text-[11px] font-bold text-slate-900 mb-1">{order.customerName}</p>
                                   <p className="text-[9px] text-slate-400 font-mono">ID: #{order.orderNumber || order.id.slice(0, 8)}</p>
                                </div>
                                <div className="text-right">
                                   <p className="text-xs font-black text-slate-900">₹{order.totalAmount}</p>
                                   <p className="text-[9px] text-blue-600 font-bold tracking-widest uppercase">{order.pincode}</p>
                                </div>
                             </div>
                           ))}
                        </div>
                     </div>
                   </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
